import { RefObject } from 'react';
import { setDefaultOptions, loadModules } from 'esri-loader';
import _cloneDeep from 'lodash/cloneDeep';
import _uniqueId from 'lodash/uniqueId';
import _debounce from 'lodash/debounce';
import moment from 'moment';
import geojsonUtil from '../utils/arcgistoGeojson.utils';

import SketchController from './SketchContoller';
import TimelineController from './TimelineController';
import HansenChartController from './HansenChartController';

import mosaics from '../mosaics.json';

import { createAnnotationPopup } from '../utils/createAnnotationPopup.utils';
import layerFactory from '../utils/layerFactory.utils';
import { colorize, colorizeHansen } from '../utils/colorize.utils';
import { createRaster } from '../utils/rasterFunctions.utils';
import { configureMeasurementObserver, configurePopupObserver } from '../utils/miscHelperFunctions.utils';
import ga, { GAActions, GACategories } from '../utils/googleAnalytics.utils';

import { DropResult } from 'react-beautiful-dnd';

import store from '../store/store';
import { setAuditArea, setCreatingRiverBoundaries } from '../reducers/auditdata/actions';
import {
  updateVisibleLayers,
  setMapReady,
  updateAnnotations,
  setDrawComplete,
  updateMyLayers,
  toggleMapLoader,
  updatePerformAuditLayers,
  storeTempGraphic,
  toggleLayerModal,
  toggleToastError,
  setSwipeToolActive,
  setRenderingRule,
  setTimeSlidersEnabled,
  updateDisabledLayers,
} from '../reducers/mapview/actions';

// import { toggleLoader } from '../../reducers/mapview/actions';

import config from '../config/config';

import { IAnnotationGraphicsInfo } from '../types/annotations';
import {
  IAuditAreaResults,
  ISentinelLayerResult,
  ILandsatLayerResult,
  IRasterImageryLayer,
  IPixelData,
  ESentinelProperties,
  ELandsatProperties,
} from '../types/layers';
import { IFeatureCollectionAPI } from '../types/shapefile';
import { IPerformAuditCriteriaData } from '../types/auditdata';
import { IImageryLayer } from '../types/timeline';
import { ResponseMessage } from '../types/enums';
import Graphic from 'esri/Graphic';

setDefaultOptions({ css: true, version: config.esri.version });

const defaultTimeExtent = {
  start: new Date('01/01/2015'),
  end: new Date(),
};

/**
 * Class used to control the behviour relating to the Map
 */
class MapController {
  _basemapGallery?: __esri.BasemapGallery;
  _coordinateConversion?: __esri.CoordinateConversion;
  _map?: __esri.Map;
  _mapview?: __esri.MapView;
  _mapLayers: Array<any> = [];
  _myLayers: Array<any> = [];
  _allSketchLayers: Array<any> = [];
  _search?: __esri.widgetsSearch;
  _auditAreaLayer?: __esri.GraphicsLayer;
  _contextualLayer?: __esri.GraphicsLayer;
  _riverLayer?: __esri.GraphicsLayer;
  _annotationSketchLayer?: __esri.GraphicsLayer;
  _imageBoundingBoxLayer?: __esri.GraphicsLayer;
  _forestBoundary?: __esri.Graphic;
  _swipe?: __esri.Swipe;
  _originalRasters: Array<any> = []; // because clipping the masks directly affects the raster layer, we want to store all rasters to have a library to refer back to
  _activeMeasurementWidget: 'area' | 'distance' | null = null;
  _areaMeasurementWidget?: __esri.AreaMeasurement2D;
  _distanceMeasurementWidget?: __esri.DistanceMeasurement2D;
  _elevationWidget?: __esri.ElevationProfile;
  _legends: Array<__esri.Legend> = [];
  _scaleBar?: __esri.ScaleBar;
  _measurementObserver: MutationObserver | null = null;
  _popupObserver: MutationObserver | null = null;
  _swipeImagesLoaded: number = 0;
  _updatingImageRenderingRule: boolean = false;
  _receivedDownloadResponse: boolean = false;
  _downloadingLayerData: boolean = false;
  _timesliderExtentLimit: number = 100000;
  _landsatTimeslider: any;
  _updatingLandsatTimeslider: boolean = false;
  _landsatTimeExtent?: __esri.TimeExtent;
  _sentinelTimeslider: any;
  _updatingSentinelTimeslider: boolean = false;
  _sentinelTimeExtent?: __esri.TimeExtent;
  _tempMin: number = Infinity;
  _tempMax: number = -Infinity;

  /**
   * Initialize the MapView and Map
   * @param domRef - the dom element to render the map onto
   */
  initializeMap = async (domRef: RefObject<any>) => {
    const [
      Map,
      MapView,
      Basemap,
      BasemapGallery,
      LocalBasemapsSource,
      GraphicsLayer,
      CoordinateConversion,
      DistanceMeasurement2D,
      AreaMeasurement2D,
      esriConfig,
      Zoom,
      ScaleBar,
      ElevationProfile,
    ] = await loadModules([
      'esri/WebMap',
      'esri/views/MapView',
      'esri/Basemap',
      'esri/widgets/BasemapGallery',
      'esri/widgets/BasemapGallery/support/LocalBasemapsSource',
      'esri/layers/GraphicsLayer',
      'esri/widgets/CoordinateConversion',
      'esri/widgets/DistanceMeasurement2D',
      'esri/widgets/AreaMeasurement2D',
      'esri/config',
      'esri/widgets/Zoom',
      'esri/widgets/ScaleBar',
      'esri/widgets/ElevationProfile',
    ]);

    const basemap = new Basemap({
      portalItem: {
        id: config.esri.map.fireflyBasemap,
      },
    });

    this._map = new Map({
      basemap: Basemap.fromId('topo-vector'),
      ground: 'world-elevation',
    });
    this._mapview = new MapView({
      map: this._map,
      container: domRef.current,
      center: [-15, 13],
      constraints: {
        minScale: 100000000,
        maxScale: 10000,
        rotationEnabled: false,
      },
      timeExtent: { ...defaultTimeExtent },
    });

    this._mapview?.ui.remove('zoom');

    this._mapview?.when(async () => {
      this._basemapGallery = new BasemapGallery({
        view: this._mapview,
        container: 'basemap-div',
        source: new LocalBasemapsSource({
          basemaps: [
            Basemap.fromId('topo-vector'),
            Basemap.fromId('osm'),
            Basemap.fromId('streets-navigation-vector'),
            Basemap.fromId('gray-vector'),
            basemap,
          ],
        }),
      });

      this._mapview?.watch('extent', _debounce(() => {
        const disabledLayers = store.getState().mapview.disabledLayers;
        let updatedDisabledLayers;

        const protectedAreasLayer = this._mapview?.map.findLayerById('protected-areas') as __esri.FeatureLayer;
        if (protectedAreasLayer) {
          if (disabledLayers.includes('protected-areas')) {
            if (this._mapview && this._mapview.scale <= protectedAreasLayer.minScale) {
              updatedDisabledLayers = disabledLayers.filter(layer => layer !== 'protected-areas');
            }
          } else {
            if (this._mapview && this._mapview.scale > protectedAreasLayer.minScale) {
              updatedDisabledLayers = [...disabledLayers, 'protected-areas'];
            }
          }
        }

        if (updatedDisabledLayers && (updatedDisabledLayers !== disabledLayers)) {
          store.dispatch(updateDisabledLayers(updatedDisabledLayers));
        }
        
        this.debouncedUpdateTimeSliders()
      }, 250, { trailing: true }))

      this._coordinateConversion = new CoordinateConversion({
        view: this._mapview,
      });

      if (this._coordinateConversion) {
        this._mapview?.ui.add(this._coordinateConversion, 'bottom-right');
      }

      this._distanceMeasurementWidget = new DistanceMeasurement2D({
        view: this._mapview,
        container: 'distance-container-div',
      });

      this._distanceMeasurementWidget?.watch('active', (active) => {
        if (active) {
          this._activeMeasurementWidget = 'distance';
          configureMeasurementObserver('#distance-container-div', this._measurementObserver);
        }
      });

      this._areaMeasurementWidget = new AreaMeasurement2D({
        view: this._mapview,
        container: 'area-container-div',
      });

      this._areaMeasurementWidget?.watch('active', (active) => {
        if (active) {
          this._activeMeasurementWidget = 'area';
          configureMeasurementObserver('#area-container-div', this._measurementObserver);
        }
      });

      this._scaleBar = new ScaleBar({
        view: this._mapview,
        unit: 'metric',
      });
      this._mapview?.ui.add(this._scaleBar!, 'bottom-right');

      // We need to keep track of our hansen layer during the following loop so we can add it at the end
      let hansen: __esri.FeatureLayer | null = null;

      // load layers, and store them in local variable.
      for (let i: number = 0; i < config.layers.length; i++) {
        const layer = await layerFactory(config.layers[i]);
        if (layer) {
          this._mapLayers.push(layer);
          if (layer.id === 'hansen') {
            layer.pixelFilter = (pixelData: IPixelData) => colorizeHansen(pixelData);
            hansen = layer;
          } else if (layer.type === 'imagery' || layer.type === 'imageryTile') {
            this._map?.add(layer, 0);
          } else {
            this._map?.add(layer);
          }
        }
      }

      if (hansen) {
        this._map?.add(hansen, this._map.allLayers.length - 1);
      }
      const tilelayer = mosaics.mosaics[mosaics.mosaics.length - 1]._links.tiles;

      this._elevationWidget = new ElevationProfile({
        view: this._mapview,
        container: 'elevation-profile-div',
        profiles: [
          {
            // displays elevation values from Map.ground
            type: 'ground', //autocasts as new ElevationProfileLineGround()
          },
        ],
        visibleElements: {
          selectButton: false,
        },
      });

      // gets the mosaics list of planet tiles from Enterprise
      // pulls the newest one and assigns to tilelayer
      this.addPlanetLayer(tilelayer);

      const visibleLayers = this._mapLayers.filter((layer) => layer.visible).map((layer) => layer.id);
      store.dispatch(updateVisibleLayers(visibleLayers));

      const zoom = new Zoom({
        view: this._mapview,
      });

      this._search = await this.initializeSearch();
      this._mapview?.ui.add(
        [
          this._search!,
          zoom,
          'basemap-selector',
          'measurement-widgets-container',
          'annotation-selector',
          'elevation-profile-tool',
          'print-map-selector',
          'swipe-selector',
        ],
        'top-right'
      );

      this._auditAreaLayer = new GraphicsLayer({
        id: 'auditAreaLayer',
        title: 'Audit Area',
      });

      if (this._auditAreaLayer) {
        this._map?.add(this._auditAreaLayer, this._map.allLayers.length - config.esri.safeTopLayerIndex); // We want to add the layer below any that must remain at the top
        this._allSketchLayers.push(this._auditAreaLayer);
        this._myLayers.push(this._auditAreaLayer);
        store.dispatch(updateMyLayers(this._myLayers));
        this.updateLayerVisibility(this._auditAreaLayer.id, true);
        SketchController.initializeSketchTool(this._mapview);
      }

      this._contextualLayer = new GraphicsLayer({
        id: 'contextualLayer',
      });

      if (this._contextualLayer) {
        this._map?.add(this._contextualLayer, this._map.allLayers.length - config.esri.safeTopLayerIndex);
        this._allSketchLayers.push(this._contextualLayer);
        SketchController.initializeContextualSketchTool(this._mapview);
      }

      this._riverLayer = new GraphicsLayer({
        id: 'riverLayer',
      });

      if (this._riverLayer) {
        this._map?.add(this._riverLayer, this._map.allLayers.length - config.esri.safeTopLayerIndex);
        this._allSketchLayers.push(this._riverLayer);
        SketchController.initializeRiverSketchTool(this._mapview);
      }

      this._annotationSketchLayer = new GraphicsLayer({
        id: 'annotationSketchLayer',
      });

      if (this._annotationSketchLayer) {
        this._map?.add(this._annotationSketchLayer, this._map.allLayers.length - config.esri.safeTopLayerIndex);
        this._allSketchLayers.push(this._annotationSketchLayer);
        SketchController.initializeAnnotationSketchTool(this._mapview, this._annotationSketchLayer);
      }

      this._imageBoundingBoxLayer = new GraphicsLayer({
        id: 'imageBBLayer',
      });

      if (this._imageBoundingBoxLayer) {
        this._map?.add(this._imageBoundingBoxLayer, this._map.allLayers.length - config.esri.safeTopLayerIndex);
        // this._mapLayers.push(this._imageBoundingBoxLayer); // temporary commenting out as _mapLayers is being used purely for reference layers and not tooling layers
      }

      if (this._mapview) {
        this._mapview.zoom = 3; //set initial mapview zoom level to cover whole container
      }

      const hansenLayer = this._map?.findLayerById('hansen');
      if (hansenLayer) {
        // here is where we store hansen layer raster because user could never create a veg index
        this._originalRasters.push({
          originalRaster: (hansenLayer as __esri.ImageryLayer)?.renderingRule?.toJSON(),
          layerId: hansenLayer.id,
        });
      }

      store.dispatch(setMapReady(true));

      //attach event handlers
      this._mapview?.on('click', this.handleMapViewClick);
      // this._mapview?.on('pointer-move', this.handleMapViewHover);

      this._mapview?.on('double-click', () => {
        console.log('double click')
      })

      this._mapview!.popup.on('trigger-action', (event: any) => {
        // this popup event action is specifically on FSC Boundary features and nothing else
        if (event.action.id === 'select-audit-area') {
          this.setAuditAreaPopupAction();
        }
      });

      // attach interceptors for raster calls
      esriConfig.request.interceptors.push({
        // set the `urls` property to the URL of the ImageryLayer so that this
        // interceptor only applies to requests made to the ImageryLayer URL
        urls: [
          config.layers.find((l) => l.options.id === 'landsat')?.options.url,
          config.layers.find((l) => l.options.id === 'sentinel')?.options.url,
          config.esri.auditPolygonDownload,
          `${config.esri.auditPolygonDownload}/jobs/`,
        ],
        after: (response: any) => {
          const currentImagePair = store.getState().auditdata.currentImagePair;
          if ((currentImagePair && (currentImagePair.before || currentImagePair.after)) && store.getState().mapview.loader && !store.getState().mapview.swipeToolActive) {
            store.dispatch(toggleMapLoader(false));
          }
          if (response.data instanceof HTMLImageElement && store.getState().mapview.swipeToolActive) {
            ++this._swipeImagesLoaded;
            if (this._swipeImagesLoaded === 2 && store.getState().mapview.loader) {
              store.dispatch(toggleMapLoader(false));
            }
          }
          if (this._updatingImageRenderingRule) {
            store.dispatch(toggleMapLoader(false));
            this._updatingImageRenderingRule = false;
          }
          if (response.data instanceof HTMLImageElement && this._updatingLandsatTimeslider) {
            if (!this._updatingSentinelTimeslider) {
              store.dispatch(toggleMapLoader(false));
            }
            this._updatingLandsatTimeslider = false;
          }
          if (response.data instanceof HTMLImageElement && this._updatingSentinelTimeslider) {
            if (!this._updatingLandsatTimeslider) {
              store.dispatch(toggleMapLoader(false));
            }
            this._updatingSentinelTimeslider = false;
          }
          if (this._downloadingLayerData) {
            store.dispatch(toggleMapLoader(false));
            this._downloadingLayerData = false;
            this._receivedDownloadResponse = true;
          }
        },
        error: (response: any) => {
          if (response && response.message === ResponseMessage.UnableToComplete) {
            const layer = this._map?.findLayerById(this._myLayers[this._myLayers.length - 1].id);
            if (layer) {
              this._map?.remove(layer);
              this._myLayers.pop();
            }
          }
          // We need better error handling here, but there's no list of errors anywehre to know what other properties/things we can check, all we can go off right now is the 'cancelled' error
          if (
            response.details.requestOptions.responseType === 'image' &&
            store.getState().mapview.swipeToolActive &&
            response.message !== ResponseMessage.RequestCanceled
          ) {
            if (store.getState().mapview.loader) {
              store.dispatch(toggleMapLoader(false));
            }
            store.dispatch(setSwipeToolActive(false));
            store.dispatch(toggleToastError(true, 'An error occured fetching Swipe Imagery.'));
          }

          if (this._updatingImageRenderingRule) {
            store.dispatch(toggleMapLoader(false));
            this._updatingImageRenderingRule = false;
            store.dispatch(toggleToastError(true, 'An error occured updating imagery rendering option.'));
          }
          if (this._updatingLandsatTimeslider && response.message !== ResponseMessage.RequestCanceled) {
            if (!this._updatingSentinelTimeslider) {
              store.dispatch(toggleMapLoader(false));
            }
            this._updatingLandsatTimeslider = false;
            if (response.message.includes('Unable to load')) {
              store.dispatch(toggleToastError(true, 'Exporting images timed out'));
            } else {
              store.dispatch(
                toggleToastError(
                  true,
                  'There is no Landsat image associated with this date. Please choose another date.'
                )
              );
            }
          }
          if (this._updatingSentinelTimeslider && response.message !== ResponseMessage.RequestCanceled) {
            if (!this._updatingLandsatTimeslider) {
              store.dispatch(toggleMapLoader(false));
            }
            this._updatingSentinelTimeslider = false;
            if (response.message.includes('Unable to load')) {
              store.dispatch(toggleToastError(true, 'Exporting images timed out'));
            } else {
              store.dispatch(
                toggleToastError(
                  true,
                  'There is no Sentinel image associated with this date. Please choose another date.'
                )
              );
            }
          }
          if (this._downloadingLayerData) {
            store.dispatch(toggleMapLoader(false));
            this._downloadingLayerData = false;
            store.dispatch(toggleToastError(true, 'An error occured downloading layer.'));
          }
        },
      });
    });
  };

  /*
   * @param layer The layer to query
   * @param where The where statement
   * @param outFields The outfields to get back
   * @param returnGeometry If we need the geometry
   */
  queryFeaturesByLayer = async (
    layer: __esri.Layer,
    where: string,
    outFields: Array<string> = ['*'],
    returnGeometry: boolean = false
  ): Promise<__esri.FeatureSet> => {
    const [Query] = await loadModules(['esri/rest/support/Query']);

    const query = new Query();
    query.where = where;
    query.outFields = outFields;
    query.returnGeometry = returnGeometry;

    return await (layer as __esri.FeatureLayer).queryFeatures(query);
  };

  /**
   * Initialize the Search widget
   */
  initializeSearch = async () => {
    const [Search, Point, Locator] = await loadModules([
      'esri/widgets/Search',
      'esri/geometry/Point',
      'esri/tasks/Locator',
    ]);

    const searchLayerID = 'fsc-certified-forests';
    const searchLayerName = 'FSC Certified Forests';

    return new Search({
      view: this._mapview,
      container: document.getElementById('search'),
      includeDefaultSources: false,
      locationEnabled: false,
      suggestionsEnabled: true,
      allPlaceholder: 'Type one or more characters',
      sources: [
        {
          layer: this._mapLayers.find((layer) => {
            return layer.id === searchLayerID;
          }),
          placeholder: `Search ${searchLayerName}`,
          name: searchLayerName,
          searchFields: ['fsc_cert', 'contributo'],
          displayField: 'fsc_cert',
          suggestionTemplate: '{contributo}: ({fsc_cert})',
          outFields: ['*'],
          popupEnabled: true,
          resultSymbol: {
            type: 'simple-marker',
            style: 'circle',
            color: 'black',
            size: '8px',
            outline: {
              color: [0, 0, 0],
              width: 3,
            },
          },
        },
        {
          locator: new Locator({ url: 'https://geocode.arcgis.com/arcgis/rest/services/World/GeocodeServer' }),
          singleLineFieldName: 'SingleLine',
          outFields: ['City', 'Region', 'Country'],
          placeholder: `Search by city, region, or country`,
          name: 'ArcGIS World Geocoding Service',
          maxSuggestions: 6,
          minSuggestCharacters: 3,
          suggestionTemplate: '{City}, {Country}',
          // categories: ['Populated Place'],
        },
      ],
      goToOverride: async (view: __esri.MapView, goToParams: any) => {
        const { geometry } = goToParams.target.target;

        const fsc_layer: __esri.Layer = this._map?.findLayerById(searchLayerID)!;

        if (goToParams.target.target.attributes.OBJECTID) {
          const whereClose = `OBJECTID = ${goToParams.target.target.attributes.OBJECTID}`;

          const layer = await this.queryFeaturesByLayer(fsc_layer, whereClose, ['*'], true);

          this._mapview?.goTo(layer.features);
          // We create a faux hitTestResult and pass it to the openPopup function to open the correct popup but only for the layers that can be searched -- (WE DON'T WANT TO DO THIS FOR WORLDGEOSERVICE)
          if (goToParams.target.target.layer) {
            // Return features do not including layer info, adding them
            layer.features[0].layer = goToParams.target.target.layer;
            this.openPopup({
              results: [
                {
                  graphic: layer.features[0],
                  mapPoint: new Point({
                    ...geometry.extent.center,
                  }),
                },
              ],
            });
            return;
          }
        } else {
          this._mapview?.goTo({
            target: goToParams.target.target,
            zoom: 15,
          });
          return;
        }
      },
    });
  };

  /**
   * Toggle the Swipe widget
   * @param destroy: boolean indicating whether to destroy the widget or not
   */
  toggleSwipeWidget = async (destroy: boolean) => {
    const { imagePairs } = store.getState().auditdata.selectedImagePairs;
    const imageRenderingRule = store.getState().mapview.imageRenderingRule;
    const swipeLayerID = IImageryLayer[imagePairs[0].before.imageLayer];
    this._swipeImagesLoaded = 0;
    if (destroy || imagePairs.length === 0) {
      if (this._swipe) {
        this._swipe.destroy();
        this._swipe = undefined;
      }
      const leadingLayer = this._map?.findLayerById('swipe-left');
      const trailingLayer = this._map?.findLayerById('swipe-right');

      // We remove leading/trailing layers when destroying the widget as we need to comfortably re-create them should a user update the images they want to analyze
      if (leadingLayer) {
        this._map?.remove(leadingLayer);
      }
      if (trailingLayer) {
        this._map?.remove(trailingLayer);
      }
      store.dispatch(setSwipeToolActive(false));
      return;
    }
    store.dispatch(setSwipeToolActive(true));
    const [Swipe, RasterFunction] = await loadModules(['esri/widgets/Swipe', 'esri/layers/support/RasterFunction']);

    store.dispatch(toggleMapLoader(true));
    // In order to perform a swipe analysis on the same layer, we have to create two versions (we can't perform swipe on a single layer)
    let leadingLayer, trailingLayer;
    let leadingLayerObject = _cloneDeep(config.layers.find((layer) => layer.options.id === swipeLayerID));
    let trailingLayerObject = _cloneDeep(config.layers.find((layer) => layer.options.id === swipeLayerID));

    if (leadingLayerObject) {
      leadingLayerObject.options.id = 'swipe-left';
      leadingLayerObject.options.visible = true;
      leadingLayer = await layerFactory(leadingLayerObject);
    }

    if (trailingLayerObject) {
      trailingLayerObject.options.id = 'swipe-right';
      trailingLayerObject.options.visible = true;
      trailingLayer = await layerFactory(trailingLayerObject);
    }

    if (leadingLayer && trailingLayer) {
      const renderingRule: __esri.RasterFunction = new RasterFunction({
        functionName: imageRenderingRule[swipeLayerID],
      });
      // Once we have our two layers, we can lock each to their respective image rasters
      leadingLayer.mosaicRule = {
        method: 'lock-raster',
        lockRasterIds: imagePairs.map((pair) => pair.before.oid),
      };
      leadingLayer.renderingRule = renderingRule;
      this._map?.add(leadingLayer, 0);

      trailingLayer.mosaicRule = {
        method: 'lock-raster',
        lockRasterIds: imagePairs.map((pair) => pair.after.oid),
      };
      trailingLayer.renderingRule = renderingRule;
      this._map?.add(trailingLayer, 1);

      this._swipe = new Swipe({
        view: this._mapview,
        leadingLayers: [leadingLayer],
        trailingLayers: [trailingLayer],
        direction: 'horizontal',
        position: 50,
      });
      if (this._swipe) {
        this._mapview?.ui.add(this._swipe);
        // We have to hide the original landsat/sentinel layer otherwise it overlays the swipe widget
        this.updateLayerVisibility(swipeLayerID, false);
      }
    }
  };

  /**
   * Toggle a layer's visibility on or off and update the Redux store
   * @param id - a layer's ID
   * @param visible: parameter to specify visibility
   */
  updateLayerVisibility = (id: string, visible: boolean) => {
    const layer = this._map?.findLayerById(id);
    if (layer) {
      if (layer.visible !== visible) {
        ga.event(GACategories.Layer, visible ? GAActions.TurnedOn : GAActions.TurnedOff, layer.title);
      }
      const visibleLayers = [...store.getState().mapview.visibleLayers];
      if (!visible && visibleLayers.includes(layer.id)) {
        // remove layer title from array
        const indexOfLayerId = visibleLayers.indexOf(layer.id);
        visibleLayers.splice(indexOfLayerId, 1);
      } else if (visible && !visibleLayers.includes(layer.id)) {
        visibleLayers.push(layer.id);
      }
      layer.visible = visible;
      store.dispatch(updateVisibleLayers(visibleLayers));
    }
  };

  /**
   * Fetch all layers on the map
   */
  get mapLayers() {
    return this._mapLayers;
  }

  /**
   * Function that returns the correct symbols for ImageryLayer Bounding Boxes
   * @param symbolType: String describing the Imagery Layer or whether symbol is a highlight symbol
   */
  getBoundingBoxSymbol = (symbolType: string) => {
    switch (symbolType) {
      case 'highlight':
        return {
          type: 'simple-line',
          color: [255, 216, 0],
          width: 2,
        };
      case 'before-image':
        return {
          type: 'simple-line',
          color: [255, 0, 255],
          width: 2,
        };
      case 'sentinel':
        return {
          type: 'simple-line',
          color: [29, 126, 240, 0.5],
          width: 1,
        };
      case 'landsat':
      default:
        return {
          type: 'simple-line',
          color: [253, 58, 81, 1],
          width: 1,
        };
    }
  };

  /**
   * Handles fetching data to display in the land cover timeline
   * @param layer The layer to fetch data from
   * @param geometry The geometry used to fetch data
   * @param options Extra options used to fetch data
   * @param features Array containing all the features from the previous call
   */
  fetchLayerData = async ({
    layer,
    geometry,
    isTimeSlider = false,
    options,
    features,
    maxCloudCover = 1,
  }: {
    layer: __esri.ImageryLayer;
    geometry: __esri.Geometry;
    isTimeSlider?: boolean;
    options?: { time: string };
    features?: Array<__esri.Graphic>;
    maxCloudCover?: number;
  }, startDate?: string): 
  Promise<any> => {
    const [Query, TimeExtent, Graphic] = await loadModules([
      'esri/rest/support/Query',
      'esri/TimeExtent',
      'esri/Graphic',
    ]);

    const query: __esri.Query = new Query();
    let outFields: Array<string>;

    if (!store.getState().leftpanel.loader) {
      store.dispatch(toggleMapLoader(true));
    }

    if (!features) {
      features = [];
    }

    switch (layer.id) {
      case 'landsat':
        outFields = [ELandsatProperties.acquisitionDate, ELandsatProperties.cloudCover, ELandsatProperties.gridLabel];
        query.where = `${ELandsatProperties.gridLabel} IS NOT NULL AND ${ELandsatProperties.cloudCover} < ${maxCloudCover}`;
        query.orderByFields = [`${ELandsatProperties.acquisitionDate} ASC`];
        break;
      case 'sentinel':
        outFields = [
          ESentinelProperties.acquisitionDate,
          ESentinelProperties.cloudCover,
          ESentinelProperties.gridLabel,
        ];
        query.where = `${ESentinelProperties.gridLabel} IS NOT NULL AND ${ESentinelProperties.cloudCover} < ${maxCloudCover}`;
        query.orderByFields = [`${ESentinelProperties.acquisitionDate} ASC`];
        break;
      default:
        outFields = [];
        break;
    }
    query.maxRecordCountFactor = 5;
    query.returnGeometry = true;
    query.outFields = outFields;
    query.geometry = geometry;

    if (!isTimeSlider) {
      if (options?.time) {
        // Paging the data from a certain date
        query.timeExtent = new TimeExtent({
          start: new Date(options.time),
          end: null,
        });
      } else if (layer.id === 'sentinel' || layer.id === 'landsat') {
        // Use the start date set by the user to fetch data starting that date
        query.timeExtent = new TimeExtent({
          // start: store.getState().leftpanel.timelineDateRange.startDate?.toDate(),
          start: startDate ? new Date(startDate) : TimelineController.initialDateRange.startDate,
          end: null,
        });
      }
    }

    const results = await layer.queryRasters(query);
    const resultLength = results.features.length;

    // Both services have different limits, and based on those if we hit the limit we know we need to hit it again
    // to fetch the next set of data. We TS-IGNORE because maxRecordCount is set to 'void' for some reason!
    // @ts-ignore
    if (resultLength === layer.capabilities.query.maxRecordCount) {
      const start = results.features[resultLength - 1].attributes[
        layer.id === 'landsat' ? ELandsatProperties.acquisitionDate : ESentinelProperties.acquisitionDate
      ];

      features = features!.concat(results.features);

      return await this.fetchLayerData({ layer, geometry, isTimeSlider, options, features }, start);
    }

    // If features exists, we can concat with the last set of features
    if (features) {
      features = features!.concat(results.features);
    } else {
      features = results.features;
    }

    return features!.map((res: __esri.Graphic) => {
      if (!isTimeSlider) {
        const boundingBoxes = this._imageBoundingBoxLayer?.graphics.map((graphic) => graphic.attributes.pr);

        const graphic = new Graphic({
          geometry: res.geometry,
          attributes: res.attributes,
          symbol: this.getBoundingBoxSymbol(layer.id),
        });

        if (layer.id === 'sentinel') {
          this._imageBoundingBoxLayer?.add(graphic);
        } else if (layer.id === 'landsat' && !boundingBoxes?.includes(res.attributes.pr)) {
          this._imageBoundingBoxLayer?.add(graphic);
        }
      }
      return res.attributes;
    });
  };

  /**
   *
   * @param id - the id to find the Bounding Box with
   * @param layer - Landsat or Sentinel layer
   * @param highlight - boolean describing whether or not to highlight
   */
  toggleBoundingBoxHighlight = (
    date: number,
    id: number | string,
    layer: string,
    highlight: boolean,
    higlightOverride?: boolean
  ) => {
    let symbolType = layer;
    // We have to keep track of our highlighted 'Before' graphic so that it doesn't get removed when user hovers over timeline points
    const imagePairs = store.getState().auditdata.selectedImagePairs.imagePairs;
    const beforeIDs = imagePairs.map((pair) => pair.before.highlightID);
    if (beforeIDs.includes(id)) {
      symbolType = higlightOverride ? 'highlight' : 'before-image';
    } else if (highlight) {
      symbolType = 'highlight';
    }
    const bbAttr = layer === 'landsat' ? 'pr' : 'tilename';
    const bbGraphic = this._imageBoundingBoxLayer?.graphics.find((graphic) => {
      if (layer === 'landsat') {
        return graphic.attributes[bbAttr] === id;
      } else {
        return graphic.attributes[bbAttr] === id && graphic.attributes.acquisitiondate === date;
      }
    });
    if (bbGraphic) {
      bbGraphic.set('symbol', this.getBoundingBoxSymbol(symbolType));
      this._imageBoundingBoxLayer?.graphics.reorder(bbGraphic, this._imageBoundingBoxLayer?.graphics.length - 1);
      // We need to keep the BeforeImage graphic at the top
      if (beforeIDs) {
        const beforeImageGraphics = this._imageBoundingBoxLayer?.graphics.filter((graphic) =>
          beforeIDs.includes(graphic.attributes[bbAttr])
        );
        if (!higlightOverride && beforeImageGraphics) {
          beforeImageGraphics.forEach((graphic) => {
            this._imageBoundingBoxLayer?.graphics.reorder(graphic, this._imageBoundingBoxLayer?.graphics.length - 1);
          });
        }
      }
    }
  };

  /**
   * Function to display selected images on the map
   * @param layerID - the ImageryLayer we want to display the image on
   * @param images - an array of OIDs for the images we want to display
   */
  addImagesToMap = async (layerID: string, images?: Array<number>) => {
    const [RasterFunction] = await loadModules(['esri/layers/support/RasterFunction']);
    const imageRenderingRule = store.getState().mapview.imageRenderingRule;
    const imageLayer = this._mapLayers.find((layer) => layer.id === layerID);

    if (imageLayer && images && images.length > 0) {
      imageLayer.mosaicRule = {
        method: 'lock-raster',
        lockRasterIds: [...images],
      };
      imageLayer.renderingRule = new RasterFunction({
        functionName: imageRenderingRule[layerID],
      });
      if (!imageLayer.visible) {
        this.updateLayerVisibility(layerID, true);
      }
    } else {
      imageLayer.mosaicRule = null;
    }
  };

  /**
   * Function used to clear Timeline Images from the map
   */
  clearImagesFromMap = () => {
    config.layers.forEach((layer) => {
      if (layer.options.id === 'landsat' || layer.options.id === 'sentinel') {
        const imageLayer = this._mapLayers.find((l) => l.id === layer.options.id);
        if (imageLayer) {
          imageLayer.mosaicRule = null;
          this.updateLayerVisibility(imageLayer.id, false);
        }
      }
    });
  };

  /**
   * Function to update an Bounding Box visibility
   * @param visible - the visibility we want to set for the layer
   * @param layerID - the ID for the layer we want to adjust
   * @param startDate - the start date to filter BBs by
   * @param endDate - the end date to filter BBs by
   */
  updateBoundingBoxImageVisibility = (
    visible: boolean,
    layerID?: string,
    startDate?: moment.Moment,
    endDate?: moment.Moment
  ) => {
    if (startDate && endDate) {
      this._imageBoundingBoxLayer?.graphics.filter((item) => {
        if (
          (item.attributes.acquisitiondate &&
            moment(item.attributes.acquisitiondate).isBetween(startDate, endDate, 'second', '[]')) ||
          item.attributes.pr ||
          item.attributes.tilename
        ) {
          item.visible = true;
          return true;
        } else {
          item.visible = false;
          return false;
        }
      });
    }
    if (layerID) {
      if (layerID === 'landsat') {
        this._imageBoundingBoxLayer?.graphics.filter((item) => {
          if (item.attributes.hasOwnProperty('pr')) {
            if (item.visible !== visible) {
              item.visible = visible;
            }
            return true;
          } else {
            return false;
          }
        });
      } else {
        this._imageBoundingBoxLayer?.graphics.filter((item) => {
          if (item.attributes.hasOwnProperty('tilename')) {
            if (item.visible !== visible) {
              item.visible = visible;
            }
            return true;
          } else {
            return false;
          }
        });
      }
    } else {
      if (this._imageBoundingBoxLayer) {
        this._imageBoundingBoxLayer?.graphics.forEach((item) => {
          if (item.visible !== visible) {
            item.visible = visible;
          }
        });
        this._imageBoundingBoxLayer.visible = visible;
      }
    }
  };

  /**
   * Function to handle what we do when the map is clicked
   * @param e - the MapClick event
   */
  handleMapViewClick = async (e: __esri.ViewClickEvent): Promise<void> => {
    if (
      store.getState().mapview.sketchToolActive ||
      store.getState().mapview.annotationToolActive ||
      store.getState().mapview.contextualSketchToolActive || 
      store.getState().mapview.riverSketchToolActive ||
      this._activeMeasurementWidget !== null ||
      this._elevationWidget?.viewModel.state === 'creating'
    ) {
      return;
    }

    const [Point] = await loadModules(['esri/geometry/Point']);
    const fscLayerId = 'fsc-certified-forests';
    let layer: __esri.FeatureSet;

    const hitTestResult: __esri.HitTestResult = await this._mapview?.hitTest(e, {
      exclude: [
        ...this._map!.basemap.baseLayers.toArray(),
        ...this._map!.basemap.referenceLayers.toArray(),
        this._map!.findLayerById('landsat'),
        this._map!.findLayerById('sentinel'),
        this._map!.findLayerById('tree-cover-loss-by-dominant-driver'),
        this._map!.findLayerById('hansen'),
      ],
    })!;
    const hitTestLayer: __esri.Layer = hitTestResult?.results[0]?.graphic?.layer;

    // We need to only query for detailed geometries if user select fsc-certified-forests
    if (hitTestLayer?.id === fscLayerId) {
      const fsc_layer: __esri.Layer = this._map?.findLayerById(fscLayerId)!;
      const whereClose = `objectid = ${hitTestResult?.results[0].graphic.attributes.objectid}`;
      layer = await this.queryFeaturesByLayer(fsc_layer, whereClose, ['*'], true);
    }

    // We create a faux hitTestResult and pass it to the openPopup function to open the correct popup but only for the layers that can be searched -- (WE DON'T WANT TO DO THIS FOR WORLDGEOSERVICE)
    if (hitTestResult?.results[0]?.graphic?.layer) {
      // Return features do not including layer info, adding them
      if (hitTestLayer?.id === fscLayerId) {
        layer!.features[0].layer = hitTestLayer!;
        this.openPopup({
          results: [
            {
              graphic: layer!.features[0],
              mapPoint: new Point({
                ...hitTestResult!.results[0].graphic.geometry.extent.center,
              }),
            },
          ],
        });
      } else {
        this.openPopup(hitTestResult);
      }
    }

    // if user clicks anything, we should ensure if any edit modes are true, we set them to false and the annotations list is updated
    const existingAnnotations = (this._map?.findLayerById('annotationSketchLayer') as __esri.GraphicsLayer).graphics;
    if (existingAnnotations.some((g) => g.attributes.editMode)) {
      const newAnnotations: Array<__esri.Graphic> = [];
      existingAnnotations.forEach((g) => {
        if (g.attributes.editMode) {
          g.attributes.editMode = false;
        }
        newAnnotations.push(g);
      });
      // UPDATE ANNOTATIONS LIST IN CASE USER CLICKS OUT AFTER CREATING ANNOTATION BUT DOESN'T SAVE
      store.dispatch(updateAnnotations(newAnnotations));
    }

    const foundAnnotationLayer = hitTestResult?.results.find(
      (res) => res.graphic && res.graphic.layer?.id === 'annotationSketchLayer'
    );
    if (foundAnnotationLayer) {
      if (SketchController._annotationSketch?.activeTool !== 'reshape') {
        this._mapview?.popup.open({
          location: (foundAnnotationLayer.graphic.geometry as __esri.Polygon).centroid,
          content: createAnnotationPopup({
            graphic: foundAnnotationLayer.graphic,
            close: () => {
              SketchController._annotationSketch?.complete();
              this._mapview?.popup.close();
            },
          }),
        });
      }
    }
  };

  /**
   * method to open popup manually
   * @param hitTestResult - HitTestResult from map click
   */
  openPopup = async (hitTestResult: __esri.HitTestResult) => {
    const [ActionToggle] = await loadModules(['esri/support/actions/ActionToggle']);

    const selectAuditAreaButton = this._mapview!.popup.actions.find((action) => action.id === 'select-audit-area');
    if (selectAuditAreaButton) {
      this._mapview!.popup.actions.remove(selectAuditAreaButton);
    }

    configurePopupObserver(false, this._popupObserver);

    const foundForestBoundariesLayer = hitTestResult?.results.find(
      (res) => res.graphic && res.graphic?.layer?.id === 'fsc-certified-forests'
    );

    if (foundForestBoundariesLayer) {
      this._forestBoundary = foundForestBoundariesLayer.graphic;

      if (!store.getState().auditdata.auditArea.graphic) {
        const selectAuditAreaAction = new ActionToggle({
          title: 'Select as Audit Area',
          id: 'select-audit-area',
          className: 'esri-icon-add-attachment',
        });

        // if (!this._mapview!.popup.actions.find((action) => action.id === 'select-audit-area')) {
        this._mapview!.popup.actions.push(selectAuditAreaAction);
        // }
      }

      if (this._search) {
        this._search.searchTerm = foundForestBoundariesLayer.graphic.attributes.fsc_cert;
      }

      this._mapview!.popup.defaultPopupTemplateEnabled = true;
      this._mapview?.popup.open({
        location: (foundForestBoundariesLayer.graphic.geometry as __esri.Polygon).centroid,
        features: [foundForestBoundariesLayer.graphic],
      });
      return;
    }
  };

  /**
   * method to toggle specific annotation into edit mode
   * @param info - object containing an annotations properties: title, desc, editMode, id
   */
  updateAnnotationGraphicsInfo = (info: IAnnotationGraphicsInfo) => {
    // set/update newly created graphic
    const existingAnnotations = (this._map?.findLayerById('annotationSketchLayer') as __esri.GraphicsLayer).graphics;

    const itemToUpdate = existingAnnotations.find((graphic) => graphic.attributes && graphic.attributes.id === info.id);
    if (itemToUpdate) {
      itemToUpdate.attributes = info;
    }

    const newAnnotations: Array<__esri.Graphic> = [];
    existingAnnotations.forEach((g) => {
      newAnnotations.push(g);
    });

    store.dispatch(updateAnnotations(newAnnotations));
  };

  /**
   * method to delete a specific annotation
   * @param id (optional) - string used to find specific graphic (only REQUIRED when deleting annotation from left panel)
   */
  deleteAnnotation = (id?: string) => {
    const existingAnnotations = (this._map?.findLayerById('annotationSketchLayer') as __esri.GraphicsLayer).graphics;

    let newAnnotations: Array<__esri.Graphic> = [];
    existingAnnotations.forEach((g) => {
      newAnnotations.push(g);
    });

    if (id) {
      const graphic = existingAnnotations.find((g) => g.attributes.id === id);
      newAnnotations = newAnnotations.filter((g) => g.attributes.id !== id);
      this._annotationSketchLayer?.remove(graphic);
    }

    if (this._mapview?.popup.open) {
      this._mapview.popup.close();
    }

    store.dispatch(updateAnnotations(newAnnotations));
  };

  /**
   * method to toggle specific annotation into edit mode
   * @param id - string used to find specific graphic
   */
  editAnnotation = (id: string) => {
    const existingAnnotations = (this._map?.findLayerById('annotationSketchLayer') as __esri.GraphicsLayer).graphics;

    const foundGraphic = existingAnnotations.find((g) => g.attributes.id === id);

    if (foundGraphic) {
      foundGraphic.attributes.editMode = true;
      SketchController.editAnnotation(foundGraphic);

      this._mapview?.popup.open({
        location: (foundGraphic.geometry as __esri.Polygon).centroid,
        content: createAnnotationPopup({
          graphic: foundGraphic,
          close: () => {
            SketchController._annotationSketch?.complete();
            this._mapview?.popup.close();
          },
        }),
      });
    }
  };

  /**
   * method to toggle specific annotation visibility
   * @param id - string used to find specific graphic
   * @param isVisible - boolean value used to toggle visibility
   */
  toggleAnnotationVisibility = (id: string, isVisible: boolean) => {
    const existingAnnotations = (this._map?.findLayerById('annotationSketchLayer') as __esri.GraphicsLayer).graphics;

    const foundGraphic = existingAnnotations.find((g) => g.attributes.id === id);

    if (foundGraphic) {
      foundGraphic.visible = isVisible;
    }
  };

  /**
   * method to get specific audit area results for landsat and sentinel data
   */
  getAuditAreaResults = async () => {
    const imageryLayers = this._mapLayers.filter((layer) => layer.id === 'landsat' || layer.id === 'sentinel');
    const geometry = this._auditAreaLayer?.graphics.find(
      (graphic) => graphic.layer.id === 'auditAreaLayer'
    ).geometry;

    const resultsObject: IAuditAreaResults = { landsatResults: [], sentinelResults: [] };

    if (geometry) {
      for (const layer of imageryLayers) {
        const results = await this.fetchLayerData({ layer, geometry });
        if (layer.id.includes('sentinel')) {
          resultsObject['sentinelResults'] = results.sort(
            (a: ISentinelLayerResult, b: ISentinelLayerResult) => a.acquisitiondate - b.acquisitiondate
          ); // ! returns data valueOf Date
        } else if (layer.id.includes('landsat')) {
          resultsObject['landsatResults'] = results.sort(
            (a: ILandsatLayerResult, b: ILandsatLayerResult) => a.acquisitiondate - b.acquisitiondate
          );
        }
        // We want to filter BoundingBoxes by date immediately
        this.updateBoundingBoxImageVisibility(
          true,
          '',
          TimelineController.initialDateRange.startDate,
          TimelineController.initialDateRange.endDate
        );
      }
    }

    return resultsObject;
  };

  deleteArea = () => {
    this._auditAreaLayer?.removeAll();
    this._imageBoundingBoxLayer?.removeAll();
    if (store.getState().mapview.drawComplete) {
      store.dispatch(setDrawComplete(false));
    }
    if (store.getState().auditdata.selectedImagePairs.imagePairs.length > 0) {
      // store.dispatch(clearImagePairs());
    }
    TimelineController.destroyChart();

    // remove raster analysis layers
    const clonedMyLayers = [...this._myLayers];
    clonedMyLayers.forEach((layer) => {
      if (layer.id.includes('raster-analysis')) {
        this.deleteLayer(layer.id);
      }
    });

    // clear perform audit analysis
    store.getState().mapview.performAuditLayers.forEach((criteria) => {
      this.removePerformAuditLayer(criteria.criteriaNumber);
    });
    store.dispatch(setAuditArea(null, 0, []));
  };

  // Handles the case where a user deletes and re-adds and audit area
  resetAuditAreaLayerIfNeeded = () => {
    if (this._auditAreaLayer && !this._myLayers.includes(this._auditAreaLayer)) {
      this._myLayers.push(this._auditAreaLayer);
      this.updateLayerVisibility(this._auditAreaLayer.id, true);
    }
  };

  processKML = async (file: any, dataType: string = 'audit') => {
    const [Polygon, Graphic] = await loadModules(['esri/geometry/Polygon', 'esri/Graphic']);

    return new Promise((resolve, reject) => {
      let geom: any = null;
      const url = 'https://ogre.adc4gis.com/convert';
      const form = new FormData();
      form.append('upload', file);
      const xmlhttp = new XMLHttpRequest();
      xmlhttp.open('POST', url);
      xmlhttp.onreadystatechange = async () => {
        if (xmlhttp.readyState === 4 && xmlhttp.status === 200) {
          const geojson = JSON.parse(xmlhttp.response);
          const feature = geojson.features[0];
          // OGRE service does return coordinates that contain 3 values when pushing a kml file,
          // This is likely due to the bug, we sanitize the response to where we only use the first two values for coordinates
          const filteredFeatures = feature.geometry.coordinates[0].map((c: any) => {
            return c.slice(0, 2);
          });
          feature.geometry.coordinates = [filteredFeatures];

          if (feature.geometry.type === 'Polygon' || feature.geometry.type === 'MultiPolygon') {
            feature.geometry.coordinates[0].forEach((coord: number[], j: number) => {
              feature.geometry.coordinates[0][j] = [coord[0], coord[1]];
            });

            const esriJson = geojsonUtil.geojsonToArcGIS(feature.geometry);
            geom = new Polygon({
              rings: esriJson.rings,
              spatialReference: { wkid: 4326 },
            });
          }

          const shapefileGraphic = new Graphic({
            geometry: geom,
            attributes: { area: null, id: null },
          });

          if (dataType === 'audit') {
            this._auditAreaLayer?.add(shapefileGraphic);
            SketchController.addBoundaryToAuditAreaSketchLayer(shapefileGraphic);
            this.resetAuditAreaLayerIfNeeded();
          } else {
            const [geometryEngine] = await loadModules(['esri/geometry/geometryEngine']);
            shapefileGraphic.symbol = SketchController._contextualSketch?.viewModel.polygonSymbol;
            shapefileGraphic.attributes.area = geometryEngine.geodesicArea(shapefileGraphic.geometry, 'hectares');
            shapefileGraphic.attributes.id = `${_uniqueId('my-layers')}-shapefile`;
            store.dispatch(storeTempGraphic(shapefileGraphic));
            store.dispatch(toggleLayerModal(true));
          }
          resolve(geom);
        } else if (xmlhttp.status > 400) {
          reject(new Error('Failed to parse'));
        }
      };
      xmlhttp.send(form);
    });
  };

  /**
   * method to create shapefile graphic
   * @param fileName - file name of shapefile package
   */
  createShapeFileGraphic = async (fileName: string, dataType: string = 'audit') => {
    const [request] = await loadModules(['esri/request']);

    return new Promise<string>(async (resolve, reject) => {
      store.dispatch(toggleMapLoader(true));
      const splitName = fileName.split('.');
      // Chrome and IE add c:\fakepath to the value - we need to remove it
      // see this link for more info: http://davidwalsh.name/fakepath
      const name = splitName[0].replace('c:\\fakepath\\', '');

      // define the input params for generate see the rest doc for details
      // https://developers.arcgis.com/rest/users-groups-and-items/generate.htm
      const params: any = {
        name: name,
        // targetSR: this._mapview?.spatialReference,
        maxRecordCount: 1000,
        enforceInputFileSizeLimit: true,
        enforceOutputJsonSizeLimit: true,
      };

      // generalize features to 10 meters for better performance
      params.generalize = true;
      params.maxAllowableOffset = 10;
      params.reducePrecision = true;
      params.numberOfDigitsAfterDecimal = 0;

      const myContent = {
        filetype: 'shapefile',
        publishParameters: JSON.stringify(params),
        f: 'json',
      };

      // use the REST generate operation to generate a feature collection from the zipped shapefile
      try {
        const response = await request(
          process.env.REACT_APP_ENVIRONMENT === 'Production'
            ? 'https://maps.fsc.org/portal/sharing/rest/content/features/generate'
            : 'https://devfscintmaps.fsc.org/portal/sharing/rest/content/features/generate',
          {
            query: myContent,
            body: document.getElementById('uploadForm'),
            responseType: 'json',
          }
        );
        const featureSet = response.data.featureCollection.layers[0].featureSet;

        if (featureSet.geometryType === 'esriGeometryPolygon' && featureSet.spatialReference.wkid === 102100) {
          if (featureSet.features.length === 1) {
            const shapeFileAdded = await this.addShapefileToMap(response.data.featureCollection, dataType);
            if (dataType !== 'audit' && !shapeFileAdded) {
              reject('File upload cancelled!');
            }
            resolve('Layer successfully uploaded!');
          } else {
            reject('Shapefile contains more than one feature. Please upload a shapefile with one feature to proceed.');
          }
        } else {
          if (featureSet.geometryType !== 'esriGeometryPolygon' && featureSet.spatialReference.wkid !== 102100) {
            reject(
              'Shapefile does not have the propery geometry and is not in the proper projection. Shapefiles need to have polygon geometry and be in Web Mercator projection (wkid: 102100).'
            );
          } else if (featureSet.geometryType !== 'esriGeometryPolygon') {
            reject('Shapefile is not a polygon.');
          } else {
            reject(
              'Shapefile is not in the proper projection. Please update file to Web Mercator projection (wkid: 102100).'
            );
          }
        }
      } catch (e) {
        console.log(e);
        reject('Error creating graphic');
      }
    });
  };

  /**
   * method used in Shapefile creation to create boundary from shapefile JSON
   * @param featureCollection - JSON response from generate REST API
   */
  addShapefileToMap = async (featureCollection: IFeatureCollectionAPI, dataType: string) => {
    const [Graphic] = await loadModules(['esri/Graphic']);

    const shapefileGraphic = Graphic.fromJSON(featureCollection.layers[0].featureSet.features[0]);

    if (dataType === 'audit') {
      this._auditAreaLayer?.add(shapefileGraphic);
      SketchController.addBoundaryToAuditAreaSketchLayer(shapefileGraphic);
      this.resetAuditAreaLayerIfNeeded();
    } else {
      const [geometryEngine] = await loadModules(['esri/geometry/geometryEngine']);
      shapefileGraphic.symbol = SketchController._contextualSketch?.viewModel.polygonSymbol;
      shapefileGraphic.attributes.area = geometryEngine.geodesicArea(shapefileGraphic.geometry, 'hectares');
      shapefileGraphic.attributes.id = `${_uniqueId('my-layers')}-shapefile`;
      store.dispatch(storeTempGraphic(shapefileGraphic));
      store.dispatch(toggleLayerModal(true));
      return true;
    }
  };

  /**
   * method used in Forest Boundary popup action to set boundary as audit area
   */
  setAuditAreaPopupAction = () => {
    if (this._forestBoundary) {
      this._auditAreaLayer?.add(this._forestBoundary);
      SketchController.addBoundaryToAuditAreaSketchLayer(this._forestBoundary);
      this._mapview!.popup.close();
      this._forestBoundary = undefined;
    }
  };

  /**
   * method used to generate Contextual River Boundary layer
   * @param includeHydrology - whether or not to include the HydrologyLayer
   */
  generateContextualRiverBoundaryLayer = async (includeHydrology: boolean) => {
    const [GraphicsLayer, Graphic, geometryEngine, SimpleFillSymbol] = await loadModules([
      'esri/layers/GraphicsLayer',
      'esri/Graphic',
      'esri/geometry/geometryEngine',
      'esri/symbols/SimpleFillSymbol',
    ]);
    store.dispatch(toggleMapLoader(true));

    let riverGraphic: __esri.Graphic | null = null;
    let allGeometries: Array<__esri.Geometry> = [];
    let bufferGraphic: __esri.Graphic | null = null;

    if (SketchController._riverSketch) {
      // If we're including the hydrology layer, intersect it with the FMU and generate an array of graphics - add them to the allGraphics array
      if (includeHydrology) {
        const intersectedHydrologyGeometries = await this.intersectFeaturedLayersWithFMU(['hydrology']);
        allGeometries = [...intersectedHydrologyGeometries];
        const hydrologyGraphics = await this.generateRiverBoundaryGraphics(intersectedHydrologyGeometries);
        riverGraphic = hydrologyGraphics[0];
      }
      const riverSketchGraphics = this._riverLayer?.graphics;
      // If we have graphics on the riverLayer, intersect them with the FMU and generate an array of graphics - add them to allGraphics
      if (this._riverLayer && riverSketchGraphics && riverSketchGraphics.length > 0) {
        const intersectedSketchGeometries = geometryEngine.intersect(
          riverSketchGraphics.toArray().map((graphic: __esri.Graphic) => graphic.geometry),
          store.getState().auditdata.auditArea.graphic!.geometry
        );
        allGeometries = [...allGeometries, ...intersectedSketchGeometries];
        const unionedGeometry = await geometryEngine.union(allGeometries);
        const combinedGraphics = await this.generateRiverBoundaryGraphics([unionedGeometry]);
        riverGraphic = combinedGraphics[0];
      }

      if (riverGraphic) {
        const bufferDistance = store.getState().auditdata.buffer.distance;
        const bufferUnit = store.getState().auditdata.buffer.unit;
        let bufferedGeom;
        // If we have a buffer distance, generate buffer graphics based on our graphics
        if (bufferDistance > 0) {
          bufferedGeom = geometryEngine.geodesicBuffer(riverGraphic.geometry, bufferDistance, bufferUnit);
          bufferGraphic = new Graphic({
            geometry: bufferedGeom,
            symbol: new SimpleFillSymbol({
              color: [1, 103, 167, 0.39],
              style: 'diagonal-cross',
            }),
          });
        }

        // Create the layer then clear out the sketch layer
        const layer = new GraphicsLayer({
          id: `${_uniqueId('my-layers')}-riparian-boundary`,
          title: `Riparian Boundary${
            bufferDistance && bufferUnit ? ` (buffered to ${bufferDistance} ${bufferUnit})` : ''
          }`,
          area: geometryEngine.geodesicArea(bufferedGeom, 'hectares'),
          graphics: [riverGraphic, bufferGraphic],
        });
        this.addLayerBeforeAnalysisLayers(layer);
        this._myLayers.push(layer);
        this.updateLayerVisibility(layer.id, true);
        store.dispatch(updateMyLayers(this._myLayers));
      }
      store.dispatch(toggleMapLoader(false));
      store.dispatch(setCreatingRiverBoundaries(false));
      this.updateLayerVisibility(this._riverLayer!.id, false);
      this._riverLayer?.removeAll();
    }
  };

  /**
   * method to generate river boundary graphics
   * @param geometries: array of geometries
   */
  generateRiverBoundaryGraphics = async (geometries: Array<__esri.Geometry>) => {
    const [Graphic] = await loadModules(['esri/Graphic']);
    return geometries.map((geometry: __esri.Geometry) => {
      return new Graphic({
        geometry,
        symbol: {
          type: 'simple-line',
          color: [0, 85, 255, 0.79],
          width: 2,
        },
      });
    });
  };

  /**
   * method to update the rendering option of a layer
   * @param layerID: layer id string
   * @param renderingOption: rendering option string
   */
  updateRenderingOption = async (layerID: string, renderingOption: string) => {
    const [RasterFunction] = await loadModules(['esri/layers/support/RasterFunction']);
    const layer = this._mapLayers.find((l) => l.id === layerID);
    if (layer && layer.renderingRule.functionName !== renderingOption) {
      // We only want to trigger the loder if the layer is visible, otherwise it'll never get turned off in our intercepters
      this._updatingImageRenderingRule = true;
      if (layer.visible) {
        store.dispatch(toggleMapLoader(true));
      }
      store.dispatch(setRenderingRule(layerID, renderingOption));
      (layer as __esri.ImageryLayer).renderingRule = new RasterFunction({
        functionName: renderingOption,
      });

      const selectedImagePairs = store.getState().auditdata.selectedImagePairs;
      const analysisLayer =
        selectedImagePairs.imagePairs.length > 0
          ? IImageryLayer[selectedImagePairs.imagePairs[0].before.imageLayer]
          : null;

      if (analysisLayer === layerID) {
        const leadingLayer = this._map?.findLayerById('swipe-left');
        const trailingLayer = this._map?.findLayerById('swipe-right');

        if (leadingLayer) {
          (leadingLayer as __esri.ImageryLayer).renderingRule = new RasterFunction({
            functionName: renderingOption,
          });
        }
        if (trailingLayer) {
          (trailingLayer as __esri.ImageryLayer).renderingRule = new RasterFunction({
            functionName: renderingOption,
          });
        }
      }
    }
  };

  public set activeMeasurementWidget(widgetType: 'area' | 'distance' | null) {
    this._activeMeasurementWidget = widgetType;
  }

  /**
   * method to print map in current state (no UI components)
   */
  printMap = async () => {
    const result = await this._mapview?.takeScreenshot({ format: 'png', width: 500 });
    if (result && result.dataUrl) {
      return result.dataUrl;
    } else {
      return '';
    }
  };

  /**
   * method to take graphic and make it into a new layer
   * @param graphic - esri graphic from sketch layer
   */
  createNewLayer = async (graphic: __esri.Graphic, buffer?: __esri.Graphic) => {
    const [GraphicsLayer] = await loadModules(['esri/layers/GraphicsLayer']);

    return new Promise<string>((resolve, reject) => {
      const layer = new GraphicsLayer({
        id: graphic.attributes.id,
        title: graphic.attributes.title,
        graphics: [buffer, graphic],
      });

      if (layer) {
        this.addLayerBeforeAnalysisLayers(layer);
        this._myLayers.push(layer);
        this.updateLayerVisibility(layer.id, true);
        const reverseLayers = Array.from(this._myLayers).reverse();
        store.dispatch(updateMyLayers(reverseLayers));
        resolve('Layer successfully created!');
      } else {
        reject('Not able to create layer');
      }
    });
  };

  deleteLayer = (id: string) => {
    const foundLayer = this._map?.findLayerById(id);

    if (foundLayer) {
      // if raster analysis was visible, we need to remove it from visibleLayers array
      const visibleLayers = [...store.getState().mapview.visibleLayers];
      const visibleLayersIndexOf = visibleLayers.indexOf(id);
      if (visibleLayersIndexOf !== -1) {
        visibleLayers.splice(visibleLayersIndexOf, 1);
        store.dispatch(updateVisibleLayers(visibleLayers));
      }

      // remove raster layer from map
      const index = this._myLayers.findIndex((layer) => layer.id === foundLayer.id);
      this._myLayers.splice(index, 1);
      store.dispatch(updateMyLayers(this._myLayers));

      if (id.includes('auditArea')) {
        this.deleteArea();
      } else {
        this._map?.remove(foundLayer);
      }
    }
  };

  addLayerBeforeAnalysisLayers = (layer: __esri.GraphicsLayer | __esri.FeatureLayer) => {
    const analysisLayerCount = this._map?.allLayers.filter((layer) =>
      layer?.id?.includes('-raster-analysis') ? true : false
    ).length;

    //We want to add new layers beneath Hansen and any raster analysis layers
    this._map?.add(
      layer,
      analysisLayerCount && analysisLayerCount > 0
        ? this._map?.allLayers.length - 1 - (analysisLayerCount + 1)
        : this._map?.allLayers.length - 1 - config.esri.safeTopLayerIndex
    );
  };

  createGroupRaster = async (vegetationIndex: string) => {
    if (this._tempMin !== Infinity && this._tempMax !== -Infinity) {
      this.resetMinMax();
    }
    const [GroupLayer] = await loadModules(['esri/layers/GroupLayer']);
    const { imagePairs } = store.getState().auditdata.selectedImagePairs;
    const layerId = `${_uniqueId('my-layers')}-group-layer-raster-analysis`;

    const rasterLayers: any[] = [];

    for (let i = 0; i < imagePairs.length; i++) {
      const layer = await this.createRasterLayer(vegetationIndex, imagePairs[i], layerId);
      rasterLayers.push(layer);
    }

    if (rasterLayers.length > 0) {
      return new Promise<string>((resolve, reject) => {
        const groupRaster = new GroupLayer({
          id: layerId,
          title: `${vegetationIndex} Analysis`,
          layers: rasterLayers,
        });
        this._map?.add(groupRaster, this._map.allLayers.length - config.esri.safeTopLayerIndex);
        this.updateLayerVisibility(groupRaster.id, true);

        const clonedLayers = [...this._myLayers];
        clonedLayers.push(groupRaster);
        this._myLayers = clonedLayers;

        store.dispatch(updateMyLayers(this._myLayers));
        resolve(`${vegetationIndex} analysis created`);

        groupRaster.on('layerview-create-error', (event: any) => {
          reject(`Unable to create ${vegetationIndex} analysis`);
        });
      });
    } else {
      return `Unable to create ${vegetationIndex} analysis`;
    }
  };

  createRasterLayer = async (vegetationIndex: string, selectedImages: any, layerId: string) => {
    const [ImageryLayer, RasterFunction] = await loadModules([
      'esri/layers/ImageryLayer',
      'esri/layers/support/RasterFunction',
    ]);

    // const { selectedImages } = store.getState().auditdata;
    const url = config.layers.find((layer) => layer.options.id === IImageryLayer[selectedImages.before!.imageLayer])
      ?.options.url;

    const afterRaster = await createRaster(
      vegetationIndex,
      selectedImages.after!.oid,
      selectedImages.after!.imageLayer
    );
    const beforeRaster = await createRaster(
      vegetationIndex,
      selectedImages.before!.oid,
      selectedImages.after!.imageLayer
    );

    const subtractedRaster = new RasterFunction({
      functionName: 'Arithmetic',
      functionArguments: {
        Raster: afterRaster, // raster function with specific image
        Raster2: beforeRaster, // raster function with specific image
        Operation: 2, //int 1 = esriRasterPlus, 2 = esriRasterMinus, 3 = esriRasterMultiply, //4= esriRasterDivide, 5=esriRasterPower, 6=esriRasterMode
      },
    });

    return new Promise<IRasterImageryLayer>((resolve, reject) => {
      store.dispatch(toggleMapLoader(true));

      const rasterLayer = new ImageryLayer({
        url,
        renderingRule: new RasterFunction({
          functionName: 'Clip',
          functionArguments: {
            ClippingGeometry: store.getState().auditdata.auditArea.graphic?.geometry, // a polygon or envelope
            ClippingType: 1, // int (1= clippingOutside, 2=clippingInside), use 1 to keep image inside of the geometry
            Raster: subtractedRaster, // raster function with specific image
          },
        }),
        id: `${_uniqueId(layerId)}`,
        pixelFilter: (pixelData: IPixelData) => {
          let minThresh = -0.1;
          let maxThresh = 0.1;
          switch (vegetationIndex) {
            case 'NDVI':
            case 'GNDVI':
              this._tempMin = -1.0;
              this._tempMax = 1.0;
              break;
            case 'SAVI':
              this._tempMin = -1.5;
              this._tempMax = 1.5;
              break;
            default:
              const stats = pixelData.pixelBlock.statistics[0];
              this._tempMin = Math.min(stats.minValue, this._tempMin);
              this._tempMax = Math.max(stats.maxValue, this._tempMax);
              const mean = (this._tempMin + this._tempMax) / 2;
              minThresh = mean - 0.1 < 0 ? mean - 0.1 : -0.1;
              maxThresh = mean + 0.1 > 0 ? mean + 0.1 : 0.1;
          }

          rasterLayer.threshold = [minThresh, maxThresh];
          rasterLayer.minValue = this._tempMin;
          rasterLayer.maxValue = this._tempMax;
          colorize(pixelData, rasterLayer.threshold);

          const count = store.getState().mapview.rasterThresholds[layerId]
            ? store.getState().mapview.rasterThresholds[layerId].count + 1
            : 1;

          store.dispatch({
            type: 'UPDATE_THRESHOLDS',
            layerId,
            thresholdValue: [minThresh, maxThresh],
            min: this._tempMin,
            max: this._tempMax,
            count,
          });
          store.dispatch(toggleMapLoader(false));
        },
      });

      rasterLayer.on('layerview-create-error', (event: any) => {
        console.log('REJECTED!');
      });

      const foundOriginalRaster = this._originalRasters.find((raster) => raster.layerId === layerId);
      if (foundOriginalRaster) {
        foundOriginalRaster.rasters.push({
          originalRaster: rasterLayer.renderingRule.toJSON(),
          layerId: rasterLayer.id,
        });
      } else {
        this._originalRasters.push({
          layerId: layerId,
          rasters: [{ originalRaster: rasterLayer.renderingRule.toJSON(), layerId: rasterLayer.id }],
        });
      }
      resolve(rasterLayer);
    });
  };

  runHansenAnalysis = async (geometry: __esri.Geometry) => {
    store.dispatch(toggleMapLoader(true));
    const data = new FormData();
    data.append('geometryType', 'esriGeometryPolygon');
    data.append('geometry', JSON.stringify(geometry.toJSON()));
    data.append('f', 'json');

    const hansenLayer = config.layers.find((layer) => layer.options.id === 'hansen');

    return fetch(`${hansenLayer?.options.url}/computeHistograms`, {
      method: 'POST',
      body: data,
    })
      .then((res) => res.json())
      .then((r) => {
        if (r.error) {
          store.dispatch(toggleToastError(true, 'Area too large - Tree Cover Loss could not be calculated'));
        }
        const counts = r.histograms.length > 0 ? r.histograms[0].counts : []; // if hansen comes back with no values (area too big, no data), we should return an [] to avoid breaking chart creation

        return counts;
      })
      .catch((err) => console.log('err', err));
  };

  intersectLayers = async (ids: Array<string | undefined>) => {
    const [Graphic, geometryEngine, SimpleFillSymbol] = await loadModules([
      'esri/Graphic',
      'esri/geometry/geometryEngine',
      'esri/symbols/SimpleFillSymbol',
    ]);
    store.dispatch(toggleMapLoader(true));

    const featuredLayerGeoms = await this.intersectFeaturedLayersWithFMU(ids);

    return new Promise<string>(async (resolve, reject) => {
      const myLayerGeoms: Array<__esri.Geometry> = [];

      for (let i = 0; i < this._myLayers.length; i++) {
        const layer = this._myLayers[i];
        if (ids.includes(layer.id)) {
          if (layer.type === 'feature') {
            const geometry = await this.generateUnionedFeatureServiceGeometry(layer);
            myLayerGeoms.push(geometry);
          } else {
            myLayerGeoms.push(layer.graphics.items[layer.id.includes('-riparian-boundary') ? 1 : 0].geometry);
          }
        }
      }

      const geometries: Array<__esri.Geometry> = [...myLayerGeoms, ...featuredLayerGeoms];

      // function to reduce geometries down into a single geometry
      const reduceGeoms = (geometries: Array<__esri.Geometry>): Array<__esri.Geometry> => {
        // the purpose of this function is to recursively whittle down an array of geometries down to one geometry where all geometries intersect with each other
        const allGeomIntersects: Array<__esri.Geometry> = []; // this array will house all unique intersected geometries

        // we loop through all geoms
        geometries.forEach((geom) => {
          const specificGeoms: Array<__esri.Geometry> = []; // this array will house all unique intersected geometries relative to the geom we are on in the first forEach
          geometries.forEach((g) => {
            const geomsSame = geometryEngine.equals(g, geom);
            const intersectedGeom = geometryEngine.intersect(g, geom);

            // don't push into specificGeoms unless they intersect and g and geom aren't the same geometry
            if (intersectedGeom && !geomsSame) {
              specificGeoms.push(intersectedGeom);
            }

            // once we make all unique geometries for this specific geom, we want to push any uniquely intersected geometries into allGeomIntersects that aren't already there
            // a geom where you intersect 2 against 1 and a geom where you intersect 1 against 2 are the same, so we don't want to include that in allGeomIntersects
            specificGeoms.forEach((geom) => {
              if (!allGeomIntersects.some((aGI) => geometryEngine.equals(geom, aGI))) {
                allGeomIntersects.push(geom);
              }
            });
          });
        });

        if (!allGeomIntersects.every((geom) => geom)) {
          // if any geoms in allGeomIntersects is null, we want return an empty array because all geoms don't intersect each other
          return [];
        } else if (allGeomIntersects.length > 1) {
          // if we have more than one geom, we need to run this new array through the function again
          return reduceGeoms(allGeomIntersects);
        } else {
          // we have one or zero geoms --> WE ARE DONE
          return allGeomIntersects;
        }
      };

      const intersectedGeom = reduceGeoms(geometries); // reduce all geometries from the layers we've selected down to 1 or 0 geometries

      // if we have one geom, we want to intersect it with the FMU
      if (intersectedGeom?.length === 1) {
        const fmuIntersectGeom = geometryEngine.intersect(
          intersectedGeom[0],
          store.getState().auditdata.auditArea.graphic?.geometry
        );

        // if our reduced geom intersects with the FMU, we want to create a new layer and send out a success message
        if (fmuIntersectGeom) {
          const area = geometryEngine.geodesicArea(fmuIntersectGeom, 'hectares');
          const intersectGraphic = new Graphic({
            geometry: fmuIntersectGeom,
            symbol: new SimpleFillSymbol({
              color: [0, 128, 128, 0.75],
              style: 'diagonal-cross',
              outline: {
                // autocasts as new SimpleLineSymbol()
                color: [0, 128, 128, 1],
                width: '1px',
              },
            }),
            attributes: {
              id: `${_uniqueId('my-layers')}-intersect`,
              area,
            },
          });

          store.dispatch(storeTempGraphic(intersectGraphic));
          store.dispatch(toggleLayerModal(true));

          resolve('Intersection layer created');
        } else {
          // if our reduced geom does not intersect, we want to send out a failure message
          reject('Selected Layers do not intersect the Forest Management Unit');
        }
      } else {
        // if we have 0 intersected geoms, we want to send a failure message
        reject('Selected Layers do not intersect');
      }
    });
  };

  mergeLayers = async (ids: Array<string | undefined>) => {
    const [Graphic, geometryEngine, SimpleFillSymbol] = await loadModules([
      'esri/Graphic',
      'esri/geometry/geometryEngine',
      'esri/symbols/SimpleFillSymbol',
    ]);
    store.dispatch(toggleMapLoader(true));

    const featuredLayerGeoms = await this.intersectFeaturedLayersWithFMU(ids);

    return new Promise<string>(async (resolve, reject) => {
      let myLayerGeoms: Array<__esri.Geometry> = [];

      for (let i = 0; i < this._myLayers.length; i++) {
        const layer = this._myLayers[i];
        if (ids.includes(layer.id)) {
          if (layer.type === 'feature') {
            const geometry = await this.generateUnionedFeatureServiceGeometry(layer);
            myLayerGeoms.push(geometry);
          } else {
            myLayerGeoms.push(layer.graphics.items[layer.id.includes('-riparian-boundary') ? 1 : 0].geometry);
          }
        }
      }

      const geometries = [...myLayerGeoms, ...featuredLayerGeoms];

      const sameGeomType = geometries.every((geom, i, arr) => geom.type === arr[0].type);
      const sameSpatialRef = geometries.every(
        (geom, i, arr) => geom.spatialReference.wkid === arr[0].spatialReference.wkid
      );

      if (sameGeomType && sameSpatialRef) {
        // all geoms must be of same type (polygon) and spatial reference (102100)
        const createdGeom = geometryEngine.union(geometries);

        const fmuIntersectGeom = geometryEngine.intersect(
          createdGeom,
          store.getState().auditdata.auditArea.graphic?.geometry
        );

        // if our unioned geom intersects with the FMU, we want to create a new layer and send a success message
        if (fmuIntersectGeom) {
          const area = geometryEngine.geodesicArea(fmuIntersectGeom, 'hectares');
          const intersectGraphic = new Graphic({
            geometry: fmuIntersectGeom,
            symbol: new SimpleFillSymbol({
              color: [0, 128, 128, 0.75],
              style: 'vertical',
              outline: {
                // autocasts as new SimpleLineSymbol()
                color: [0, 128, 128, 1],
                width: '1px',
              },
            }),
            attributes: {
              id: `${_uniqueId('my-layers')}-merge`,
              area,
            },
          });

          store.dispatch(storeTempGraphic(intersectGraphic));
          store.dispatch(toggleLayerModal(true));

          resolve('Selected Layers have been merged');
        } else {
          // if our unioned geom does not intersect with the FMU, we want to send out a failure message
          reject('Selected Layers do not intersect the Forest Management Unit');
        }
      } else {
        // if our geoms aren't of the same type (polygon) or have the same spatial reference (102100), we want to send out a failure message
        if (!sameGeomType && !sameSpatialRef) {
          // failure message for errors with both geom type and geom spatial reference
          reject('Selected Layers do not have the same Geometry Type or Spatial Reference');
        } else if (!sameSpatialRef) {
          // failure message for errors with geom spatial reference
          reject('Selected Layers do not have the same Spatial Reference');
        } else {
          // failure message for errors with geom type
          reject('Selected Layers do not have the same Geometry Type');
        }
      }
    });
  };

  intersectFeaturedLayersWithFMU = async (ids: Array<string | undefined>) => {
    const [geometryEngine, Query, execute] = await loadModules([
      'esri/geometry/geometryEngine',
      'esri/rest/support/Query',
      'esri/rest/query/executeQueryJSON',
    ]);

    const featuredLayers: Array<__esri.Geometry> = [];

    const layerTypesToMatchBy = ['map-image', 'tile'];

    if (this._mapLayers.filter((layer) => ids.includes(layer.id)).length > 0) {
      // if we have featured layers selected for merge/intersect, we need to query the feature layer for any features that intersect with the FMU geometry
      // because we have multiple queries, we put each layer's queryFeatures promise in an array to use with Promise.all to resolve them together
      const promArray = this._mapLayers
        .filter((layer) => ids.includes(layer.id))
        .map((layer) => {
          let query;
          if (layerTypesToMatchBy.includes(layer.type)) {
            query = new Query();
          } else {
            query = layer.createQuery();
          }

          query.geometry = store.getState().auditdata.auditArea.graphic!.geometry; // the point location of the pointer
          query.spatialRelationship = 'intersects'; // this is the default
          query.returnGeometry = true;
          query.outFields = ['*'];

          // query for feature intersect with audit area, union all feature geoms, return geoms
          if (layer.id === 'hydrology' || layerTypesToMatchBy.includes(layer.type)) {
            const options = config.layers.find((layerConfig) => layerConfig.options.id === layer.id)!.options;
            let url = options.optionalQueryLayer || `${options.url}/0`;

            return execute.executeQueryJSON(url, query);
          } else {
            return layer.queryFeatures(query);
          }
        });

      await Promise.all(promArray).then((res) => {
        res.forEach((r) => {
          // for each feature in a layer returned to us, we need to intersect those feature geoms with the FMU geom to get just a geometry inside the FMU
          if (r.features.length > 0) {
            // ignore any layers that don't have returned reatures
            const intersectedWithFMU = geometryEngine.intersect(
              r.features.map((feature: __esri.Graphic) => feature.geometry),
              store.getState().auditdata.auditArea.graphic!.geometry
            );
            featuredLayers.push(geometryEngine.union(intersectedWithFMU)); // union all features into one geom and push created geom into an array to return for use in the merge/intersect methods
          }
        });
      });
    }

    return featuredLayers;
  };

  // updateRasterThreshold = (threshold: Array<number>, id: string) => {
  //   const layer = this._map?.findLayerById(id) as IRasterImageryLayer;
  //   if (layer) {
  //     layer.pixelFilter = (pixelData: IPixelData) => colorize(pixelData, threshold);
  //     layer.threshold = threshold;
  //     store.dispatch(updateMyLayers(this._myLayers));
  //   }
  // };

  updateGroupRasterThreshold = (threshold: Array<number>, id: string) => {
    const groupLayer = this._map?.findLayerById(id) as __esri.GroupLayer;
    if (groupLayer) {
      groupLayer.layers.forEach((layer: any) => {
        layer.pixelFilter = (pixelData: IPixelData) => colorize(pixelData, threshold);
        layer.threshold = threshold;
        store.dispatch(updateMyLayers(this._myLayers));
      });
    }
  };

  // method used to create or toggle on a criterion mask
  applyPerformAuditMask = async (criteriaNumber: string, geomLayerId: string | null = null) => {
    const [RasterFunction, geometryEngine] = await loadModules([
      'esri/layers/support/RasterFunction',
      'esri/geometry/geometryEngine',
    ]);

    const maskLayers = this._myLayers.filter((layer) => layer.id.includes('raster-analysis'));
    const hansenLayer = this._mapLayers.find((layer) => layer.id === 'hansen');
    if (hansenLayer) {
      maskLayers.push(hansenLayer);
    }

    const auditDataLayers = store.getState().mapview.performAuditLayers;

    const newAuditDataLayers = [...auditDataLayers];

    const foundAuditDataLayer = auditDataLayers.find((layer) => layer.criteriaNumber === criteriaNumber);

    return new Promise<string>(async (resolve, reject) => {
      store.dispatch(toggleMapLoader(true));

      // if !foundAuditDataLayer we need to make the new mask logic
      if (!foundAuditDataLayer) {
        // if the selected layer for the criteria isn't visible, let's turn it on
        if (!store.getState().mapview.visibleLayers.includes(geomLayerId!)) {
          this.updateLayerVisibility(geomLayerId!, true);
        }

        let geomLayer = this._myLayers.find((layer) => layer.id === geomLayerId);
        let geometry: __esri.Geometry;
        let clippedGeom: __esri.Geometry | null = null;

        if (!geomLayer) {
          // would happen if the user selected the relevant data layer and not a specific geometry from the dropdown in criterion-selection panel
          const featureLayer = this._map?.findLayerById(geomLayerId!);
          const featureLayerGeoms = await this.intersectFeaturedLayersWithFMU([geomLayerId!]); // take that feature layer, query all features that are within FMU, union those features geoms, intersect with FMU geom
          geometry = featureLayerGeoms[0];
          geomLayer = { id: geomLayerId, title: featureLayer?.title, graphics: { items: [{ geometry }] } };
        } else {
          if (geomLayer.type === 'feature') {
            geometry = await this.generateUnionedFeatureServiceGeometry(geomLayer);
          } else {
            geometry = geomLayer.graphics.items[geomLayerId?.includes('-riparian-boundary') ? 1 : 0].geometry; // for riparian boundaries, the polyline is in the 0 index position and the polygon is in the 1 index position
          }
        }

        // Set default area and counts - if no intersection, we want to use these in the report
        let area = 0;
        let intersecting = true;
        let counts = [];

        if (geometry) {
          clippedGeom = geometryEngine.intersect(geometry, store.getState().auditdata.auditArea!.graphic!.geometry); // intersect context data with FMU (drawn areas wouldn't be clipped to FMU by default)

          if (clippedGeom) {
            counts = await this.runHansenAnalysis(clippedGeom); // get hansen pixel loss
            area = geometryEngine.geodesicArea(clippedGeom, 'hectares');

            if (maskLayers.length > 0) {
              maskLayers.forEach((maskLayer) => {
                if (maskLayer.id === 'hansen') {
                  const raster = this._originalRasters.find((raster) => raster.layerId === 'hansen').originalRaster;
                  // grab the correct IncludedRanges from the raster -- if a rinciple audit mask is applied, its inside functionArguments.Raster.rasterFunctionArguments otherwise its just in functionArguments (no principle audit mask applied)
                  raster.rasterFunctionArguments.IncludedRanges =
                    maskLayer.renderingRule.functionArguments.hasOwnProperty('IncludedRanges')
                      ? maskLayer.renderingRule.functionArguments.IncludedRanges
                      : maskLayer.renderingRule.functionArguments.Raster.rasterFunctionArguments.IncludedRanges;
                  // loop through all masks and apply new geom to them
                  maskLayer.renderingRule = new RasterFunction({
                    functionName: 'Clip',
                    functionArguments: {
                      ClippingGeometry: clippedGeom, // a polygon or envelope
                      ClippingType: 1, // int (1= clippingOutside, 2=clippingInside), use 1 to keep image inside of the geometry
                      Raster: raster, // raster function with specific image
                    },
                  });
                } else {
                  const originalRaster = this._originalRasters.find((raster) => raster.layerId === maskLayer.id);
                  maskLayer.layers.items.forEach((layer: any) => {
                    const raster = originalRaster.rasters.find((r: any) => r.layerId === layer.id).originalRaster;
                    layer.renderingRule = new RasterFunction({
                      functionName: 'Clip',
                      functionArguments: {
                        ClippingGeometry: clippedGeom, // a polygon or envelope
                        ClippingType: 1, // int (1= clippingOutside, 2=clippingInside), use 1 to keep image inside of the geometry
                        Raster: raster, // raster function with specific image
                      },
                    });
                  });
                }
              });
            }
          }
        }

        if (!clippedGeom || !geometry) {
          area = 0;
          intersecting = false;
          this.removePerformAuditMask();
        }

        const newAuditDataLayer: IPerformAuditCriteriaData = {
          criteriaNumber,
          selectedLayer: geomLayer,
          visible: true,
          hansenCounts: counts,
          area,
          intersecting,
        };

        newAuditDataLayers.forEach((layer) => (layer.visible = false));

        newAuditDataLayers.push(newAuditDataLayer);

        store.dispatch(updatePerformAuditLayers(newAuditDataLayers));

        resolve(
          geometry && clippedGeom
            ? `Now displaying a mask for Criterion ${criteriaNumber}`
            : `The ${geomLayer.title} layer does not overlap with your audit area`
        );
      } else {
        // mask logic exists so we just need to apply that to the imagery layers
        let layerOn = false;

        // loop through all audit criterions that have been created in app
        for (let i = 0; i < newAuditDataLayers.length; i++) {
          const layer = newAuditDataLayers[i];
          if (layer.criteriaNumber === criteriaNumber) {
            // if the one we are toggling, update visibility to the opposite of what it is
            layerOn = !layer.visible;
            layer.visible = !layer.visible;

            // clip geometry based on FMU
            let geometry;
            if (layer.selectedLayer.type === 'feature') {
              geometry = await this.generateUnionedFeatureServiceGeometry(layer.selectedLayer);
            } else {
              geometry =
                layer.selectedLayer.graphics.items[layer.selectedLayer.id?.includes('-riparian-boundary') ? 1 : 0]
                  .geometry; // for riparian boundaries, the polyline is in the 0 index position and the polygon is in the 1 index position
            }
            const clippedGeom = geometryEngine.intersect(
              geometry,
              store.getState().auditdata.auditArea.graphic!.geometry
            );

            if (layer.visible) {
              // if the selected layer for the criteria isn't visible, let's turn it on
              if (!store.getState().mapview.visibleLayers.includes(layer.selectedLayer.id)) {
                this.updateLayerVisibility(layer.selectedLayer.id, true);
              }

              // if mask is visible, apply clipping to all the layers
              if (maskLayers.length > 0) {
                maskLayers.forEach((maskLayer) => {
                  if (maskLayer.id === 'hansen') {
                    const raster = this._originalRasters.find((raster) => raster.layerId === 'hansen').originalRaster;
                    // grab the correct IncludedRanges from the raster -- if a rinciple audit mask is applied, its inside functionArguments.Raster.rasterFunctionArguments otherwise its just in functionArguments (no principle audit mask applied)
                    raster.rasterFunctionArguments.IncludedRanges =
                      maskLayer.renderingRule.functionArguments.hasOwnProperty('IncludedRanges')
                        ? maskLayer.renderingRule.functionArguments.IncludedRanges
                        : maskLayer.renderingRule.functionArguments.Raster.rasterFunctionArguments.IncludedRanges;
                    maskLayer.renderingRule = new RasterFunction({
                      functionName: 'Clip',
                      functionArguments: {
                        ClippingGeometry: clippedGeom, // a polygon or envelope
                        ClippingType: 1, // int (1= clippingOutside, 2=clippingInside), use 1 to keep image inside of the geometry
                        Raster: raster, // raster function with specific image
                      },
                    });
                  } else {
                    const originalRaster = this._originalRasters.find((raster) => raster.layerId === maskLayer.id);
                    maskLayer.layers.items.forEach((layer: any) => {
                      const raster = originalRaster.rasters.find((r: any) => r.layerId === layer.id).originalRaster;
                      layer.renderingRule = new RasterFunction({
                        functionName: 'Clip',
                        functionArguments: {
                          ClippingGeometry: clippedGeom, // a polygon or envelope
                          ClippingType: 1, // int (1= clippingOutside, 2=clippingInside), use 1 to keep image inside of the geometry
                          Raster: raster, // raster function with specific image
                        },
                      });
                    });
                  }
                });
              }
            } else {
              // if toggling criterion off, we want to remove mask
              this.removePerformAuditMask();
            }
          } else {
            // these layers aren't the one we've toggled, so they need to be defaulted to off
            if (layer.visible) {
              layer.visible = false;
            }
          }
        }
        store.dispatch(updatePerformAuditLayers(newAuditDataLayers));
        resolve(`${layerOn ? 'Turning on a mask' : 'Turning off a mask'} for Criterion ${criteriaNumber}`);
      }
    });
  };

  // turn off all masks by resetting raster layers to their original rasters
  removePerformAuditMask = () => {
    const maskLayers = this._myLayers.filter((layer) => layer.id.includes('raster-analysis'));
    maskLayers.push(this._mapLayers.find((layer) => layer.id === 'hansen'));

    if (maskLayers.length > 0) {
      maskLayers.forEach((maskLayer) => {
        if (maskLayer.id === 'hansen') {
          const raster = this._originalRasters.find((raster) => raster.layerId === 'hansen').originalRaster;
          if (raster) {
            const includedRanges = maskLayer.renderingRule.functionArguments.Raster
              ? maskLayer.renderingRule.functionArguments.Raster.rasterFunctionArguments.IncludedRanges
              : maskLayer.renderingRule.functionArguments.IncludedRanges;
            raster.rasterFunctionArguments.IncludedRanges = includedRanges;
            maskLayer.renderingRule = raster;
          }
        } else {
          const originalRaster = this._originalRasters.find((raster) => raster.layerId === maskLayer.id);
          maskLayer.layers.items.forEach((layer: any) => {
            const raster = originalRaster.rasters.find((r: any) => r.layerId === layer.id).originalRaster;
            if (raster) {
              layer.renderingRule = raster;
            }
          });
        }
      });
    }
  };

  // method used to remove a criterion process from the Perform Audit panel
  removePerformAuditLayer = (criteriaId: string) => {
    const auditDataLayers = store.getState().mapview.performAuditLayers;

    const newAuditDataLayers = [...auditDataLayers];

    if (newAuditDataLayers.find((layer) => layer.criteriaNumber === criteriaId)?.visible) {
      // only remove mask if the user is resetting the active mask
      this.removePerformAuditMask();
    }

    const criteriaIndex = newAuditDataLayers.findIndex((criteriaLayer) => criteriaLayer.criteriaNumber === criteriaId);

    newAuditDataLayers.splice(criteriaIndex, 1);

    store.dispatch(updatePerformAuditLayers(newAuditDataLayers));

    HansenChartController.destroyChart(criteriaId);
  };

  /**
   * Method to check whether a given string is a valid title for createing a new layer
   * @param title - String used for the layer title
   */
  checkValidLayerTitle = (title: string | null): string => {
    if (!title) {
      return 'Layers cannot be added without a valid title';
    }
    if (this._myLayers.find((layer) => layer.title.toLowerCase() === title.toLowerCase())) {
      return `The title "${title.toLowerCase()}" is already taken`;
    }
    if (title.length > config.esri.maxLayerTitleLength) {
      return `Layer titles must be ${config.esri.maxLayerTitleLength} characters or less`;
    }
    return title;
  };

  zoomToAuditArea = (graphic: __esri.Graphic) => {
    this._mapview?.goTo(graphic);
  };

  updateHansenVisibleYears = async (values: Array<number>) => {
    const [RasterFunction] = await loadModules(['esri/layers/support/RasterFunction']);

    const hansenLayer = this._map?.findLayerById('hansen');

    if (hansenLayer) {
      // grab mask rendering rule from config and update IncludedRanges value
      const hansenConfig = config.layers.find((layer) => layer.options.id === 'hansen');
      hansenConfig!.options.renderingRule.rasterFunctionArguments.IncludedRanges = values;

      if ((hansenLayer as __esri.ImageryLayer).renderingRule.functionName === 'Clip') {
        // if raster is already clipped, we need to retain that clipped geom
        (hansenLayer as __esri.ImageryLayer).renderingRule = new RasterFunction({
          functionName: 'Clip',
          functionArguments: {
            ClippingGeometry: (hansenLayer as __esri.ImageryLayer).renderingRule.functionArguments.ClippingGeometry, // a polygon or envelope
            ClippingType: 1, // int (1= clippingOutside, 2=clippingInside), use 1 to keep image inside of the geometry
            Raster: hansenConfig?.options.renderingRule,
          },
        });
      } else {
        // raster not clipped, just update values
        (hansenLayer as __esri.ImageryLayer).renderingRule = new RasterFunction(hansenConfig?.options.renderingRule);
      }
    }
    HansenChartController.updateHansenChart(store.getState().mapview.performAuditLayers, values);
  };

  fetchLegendInfo = async (container: HTMLElement, id: string) => {
    const [Legend] = await loadModules(['esri/widgets/Legend']);

    if (!container) {
      return;
    }

    const layer = this._map?.findLayerById(id);
    const legend = new Legend({
      view: this._mapview,
      container: container,
      respectLayerVisibility: false,
      layerInfos: [{ layer, hideLayers: [] }],
    });
    this._legends.push(legend);
  };

  resetMinMax = () => {
    this._tempMin = Infinity;
    this._tempMax = -Infinity;
  };

  reorderMyLayers = (result: DropResult, myLayers: Array<__esri.Layer>) => {
    if (!result.destination) return;
    const [removed] = myLayers.splice(result.source.index, 1);
    myLayers.splice(result.destination.index, 0, removed);

    const reverseLayers = Array.from(myLayers);
    reverseLayers.reverse().forEach((layer: __esri.Layer, index: number) => {
      const selectLayer = this._map!.findLayerById(layer.id);
      this._map!.reorder(selectLayer, this._map!.layers.length - config.esri.safeTopLayerIndex);
    });
  };

  setLayerDesign = (layer: __esri.GraphicsLayer, renderer: any) => {
    if (!layer.graphics) return;
    layer.graphics.forEach((el: any) => (el.symbol = {
      ...renderer,
      type: el.symbol.type
    }));
  };

  esriQuery = async (url: string, params: __esri.QueryProperties): Promise<__esri.FeatureSet | undefined> => {
    const [Query, execute] = await loadModules(['esri/rest/support/Query', 'esri/rest/query/executeQueryJSON']);
    const q: __esri.Query = new Query({ ...params });
    return await execute
      .executeQueryJSON(url, q)
      .then((result: __esri.FeatureSet) => result)
      .catch((e: Error) => {
        //console.log(e);
        return undefined;
      });
  };
  addFeatureService = async (featureServiceUrl: string) => {
    const [FeatureLayer] = await loadModules(['esri/layers/FeatureLayer']);

    store.dispatch(toggleMapLoader(true));

    return new Promise<string>(async (resolve, reject) => {
      const url = featureServiceUrl.split('?')[0];
      const urlSplit = url.split('/');
      const featureServiceIndex = urlSplit.findIndex((el: string) => el === 'FeatureServer');

      // create new feature service id with title
      const featureService = new FeatureLayer(url);
      featureService.id = featureService.title.replace(' ', '-');
      const layer = this._map?.findLayerById(featureService.id);
      // determine the feature service item index
      const featureNumber = parseInt(urlSplit[featureServiceIndex + 1]);
      // layer is already on the map. Determines by lookin at all of the unique layer id's

      // if 'FeatureServer' is not in the URL will return with an error
      if (featureServiceIndex === -1) {
        reject('URL needs to be Feature Service');
      } else if (isNaN(featureNumber)) {
        reject('URL needs to be single Feature Service');
      } else if (layer) {
        reject('Feature Service with the same name is already on the map!');
      }
      // all checks pass add data to map and mylayers through redux
      else {
        // CALCULATE AREA - commenting out for now cos we don't want it but might in the future
        // const geometry = await this.generateUnionedFeatureServiceGeometry(featureService);
        // const area = await this.calculateFeatureServiceArea(geometry);
        // featureService.area = area;

        this._map?.add(featureService);
        this._myLayers.push(featureService);
        store.dispatch(updateMyLayers(this._myLayers));
        this.updateLayerVisibility(featureService.id, true);
        store.dispatch(toggleMapLoader(false));
        resolve('Feature Service Added to Map!');
      }
    });
  };

  generateUnionedFeatureServiceGeometry = async (layer: __esri.FeatureLayer) => {
    const [GeometryEngine] = await loadModules(['esri/geometry/geometryEngine']);
    const featureSet = await layer.queryFeatures({ outFields: [], where: '1=1', returnGeometry: true });
    const features = featureSet.features;
    return await GeometryEngine.union(features.map((feature) => feature.geometry));
  };

  calculateFeatureServiceArea = async (geometry: __esri.Geometry) => {
    const [GeometryEngine] = await loadModules(['esri/geometry/geometryEngine']);
    return await GeometryEngine.geodesicArea(geometry, 'hectares');
  };

  downloadFeatureLayer = async (graphicsLayer: __esri.GraphicsLayer, out_name: string, setDownloadUrl: Function) => {
    store.dispatch(toggleMapLoader(true));
    const [geoprocessor, FeatureSet] = await loadModules(['esri/rest/geoprocessor', 'esri/rest/support/FeatureSet']);
    const featureSet = await new FeatureSet({ features: graphicsLayer.graphics.toArray() });

    let fileName = out_name.replace(/[&\\#, +()$~%.'":*?<>{}]/g, '_');
    fileName = isNaN(parseInt(fileName.slice(0, 1))) ? fileName : '_' + fileName;
    const params = {
      feature_collection: featureSet,
      out_name: fileName,
      output_file: 'download',
    };

    try {
      const job = await geoprocessor.submitJob(config.esri.auditPolygonDownload, params);

      let result;

      let options = {
        interval: 1500,
        statusCallback: async (j: __esri.JobInfo) => {
          console.log('Job Status: ', j.jobStatus);
        },
      };

      this._downloadingLayerData = true;
      store.dispatch(toggleMapLoader(true));
      this._receivedDownloadResponse = false;

      await job.waitForJobCompletion(job.jobId, options);

      this._receivedDownloadResponse = true;

      result = await job.fetchResultData('output_file');
      setDownloadUrl(result.value.url);
    } catch (e) {
      console.error(e);
    }
  };

  getMosaicsList = async () => {
    const [FeatureLayer] = await loadModules(['esri/layers/FeatureLayer']);

    const layer = new FeatureLayer({
      url: 'https://maps.fsc.org/server/rest/services/Hosted/Planet_Imagery_Points_View/FeatureServer',
    });

    const features = await layer.queryFeatures({ outFields: '*', where: '1=1' });
    const api_key = 'dd290ea73fd345a2b284641292b62e45';

    const updatedList = await features.features.map((item: Graphic) => {
      return {
        ...item.attributes,
        imagery_date: new Date(item.attributes.imagery_date),
        url_quads: item.attributes.url_quads.replace('%API_KEY%', api_key),
        url_tiles: item.attributes.url_tiles + api_key,
      };
    });

    return await updatedList;
  };

  addPlanetLayer = async (urlTemplate: string, isOn: boolean = false) => {
    const [WebTileLayer] = await loadModules(['esri/layers/WebTileLayer']);

    // if planet-images layer is on map remove
    const planetLayer = this._map?.findLayerById('planet-images');
    if (planetLayer) this._map?.layers.remove(planetLayer);

    try {
      const layer = new WebTileLayer({
        urlTemplate: decodeURI(urlTemplate),
        subDomains: ['a', 'b', 'c', 'd'],
        id: 'planet-images',
      });
      if (layer) {
        this._map?.add(layer, -1);
        this.updateLayerVisibility(layer.id, isOn);
      }
    } catch (e) {
      console.error(e);
      console.warn('Planet Earth Image Failed to load!');
    }
  };

  getMapScale = () => {
    return this._mapview?.scale;
  };

  getMapViewTime = () => {
    return {
      start: moment(this._mapview!.timeExtent.start).startOf('day').format('MM-DD-YYYY'),
      end: moment(this._mapview!.timeExtent.end).endOf('day').format('MM-DD-YYYY'),
    };
  };

  updateTimeSliders = async () => {
    // We don't want to update the time slider if the user is interacting with the timeline
    if (store.getState().auditdata.timelineOpen) {
      store.dispatch(setTimeSlidersEnabled(false));
      return;
    }
    const [TimeExtent] = await loadModules(['esri/TimeExtent']);

    const landsat = this._mapLayers.find((layer) => layer.id === 'landsat');
    const sentinel = this._mapLayers.find((layer) => layer.id === 'sentinel');

    const layers = [landsat, sentinel];

    if (layers.every((layer) => !layer)) { return; }

    if (this._mapview?.extent!.height) {
      if (this._mapview?.extent!.height <= this._timesliderExtentLimit) {
        store.dispatch(setTimeSlidersEnabled(true));
        store.dispatch(toggleMapLoader(true));

        const mapViewTimeExtent = this.getMapViewTime();

        for (const layer of layers) {
          if (layer.id === 'landsat') {
            this._updatingLandsatTimeslider = true;

            if (!this._landsatTimeExtent) {
              this._landsatTimeExtent = new TimeExtent({
                start: mapViewTimeExtent.start,
                end: mapViewTimeExtent.end,
              });
              if (!layer.visible) {
                this._updatingLandsatTimeslider = false;
              }
            }

            const results = await this.fetchLayerData({
              layer: landsat,
              geometry: this._mapview?.extent,
              isTimeSlider: true,
              maxCloudCover: 0.2,
            });

            const landsatDates = results.map((feature: ILandsatLayerResult) => new Date(feature.acquisitiondate));

            if (!this._landsatTimeslider) {
              await this.setupTimeSliders(
                layer.id,
                landsatDates
              );
            } else {
              this._landsatTimeslider.stops = {dates: landsatDates};
            }

            store.dispatch(setTimeSlidersEnabled(true));

            if (layer.visible && results.length === 0) {
              store.dispatch(toggleMapLoader(false));
              this._updatingLandsatTimeslider = false;
              store.dispatch(
                toggleToastError(true, 'There is no image associated with this date. Please choose another date.')
              );
              return;
            }

            if (layer.visible) {
              const imagesFilteredByTimeExtent = results.filter((image: ILandsatLayerResult) => {
                const start = moment(this._landsatTimeExtent?.start).startOf('day');
                const end = moment(this._landsatTimeExtent?.end).endOf('day');
                return moment(image.acquisitiondate).isBetween(start, end);
              });
              await this.addImagesToMap(
                layer.id,
                imagesFilteredByTimeExtent.map((feature: ILandsatLayerResult) => feature.objectid)
              );
            } else {
              this._updatingLandsatTimeslider = false;
              if (!this._updatingSentinelTimeslider && !sentinel.visible) {
                store.dispatch(toggleMapLoader(false));
              }
            }
          } else {
            this._updatingSentinelTimeslider = true;
            if (!this._sentinelTimeExtent) {
              this._sentinelTimeExtent = new TimeExtent({
                start: mapViewTimeExtent.start,
                end: mapViewTimeExtent.end,
              });
              if (!layer.visible) {
                this._updatingSentinelTimeslider = false;
              }
            }

            const results = await this.fetchLayerData({
              layer: sentinel,
              geometry: this._mapview?.extent,
              isTimeSlider: true,
              maxCloudCover: 0.2,
            });

            const sentinelDates: Array<string> = [];
            const sentinelDatesSet: Set<string> = new Set();

            results.forEach((feature: ISentinelLayerResult) => {
              sentinelDatesSet.add(moment(new Date(feature.acquisitiondate)).startOf('day').format('YYYY-MM-DD'));
            });

            sentinelDatesSet.forEach((date: string) => sentinelDates.push(date));

            const sentinelDateMap = sentinelDates.map((date) => moment(date).toDate())

            if (!this._sentinelTimeslider) {
              await this.setupTimeSliders(
                layer.id,
                sentinelDateMap
              );
            } else {
              this._sentinelTimeslider.stops = {dates: sentinelDateMap};
            }

            store.dispatch(setTimeSlidersEnabled(true));

            if (layer.visible && results.length === 0) {
              store.dispatch(toggleMapLoader(false));
              this._updatingSentinelTimeslider = false;
              store.dispatch(
                toggleToastError(true, 'There is no image associated with this date. Please choose another date.')
              );
              return;
            }

            if (layer.visible) {
              const imagesFilteredByTimeExtent = results.filter((image: ISentinelLayerResult) => {
                const start = moment(this._sentinelTimeExtent?.start).startOf('day');
                const end = moment(this._sentinelTimeExtent?.end).endOf('day');
                return moment(image.acquisitiondate).isBetween(start, end);
              });
              await this.addImagesToMap(
                layer.id,
                imagesFilteredByTimeExtent.map((feature: ISentinelLayerResult) => feature.objectid)
              );
            } else {
              this._updatingSentinelTimeslider = false;
              if (!this._updatingLandsatTimeslider && !landsat.visible) {
                store.dispatch(toggleMapLoader(false));
              }
            }
          }
        }
      } else {
        for (const layer of layers) {
          this.addImagesToMap(layer.id, []); // reset layer
        }
        store.dispatch(setTimeSlidersEnabled(false));
        store.dispatch(toggleMapLoader(false));
      }
    } else {
      for (const layer of layers) {
        this.addImagesToMap(layer.id, []); // reset layer
      }
      store.dispatch(setTimeSlidersEnabled(false));
      store.dispatch(toggleMapLoader(false));
    }
  };

  debouncedUpdateTimeSliders = _debounce(this.updateTimeSliders, 250, { trailing: true });

  setupTimeSliders = async (layerId: string, dates: Array<Date>) => {
    const [TimeSlider] = await loadModules(['esri/widgets/TimeSlider']);
    let { start, end } = defaultTimeExtent;

    if (layerId === 'landsat') {
      let settingUpLandsatSlider = true;
      if (this._landsatTimeslider) {
        this._landsatTimeslider.destroy();
      }
      this._landsatTimeslider = new TimeSlider({
        container: 'landsat',
        view: this._mapview,
        mode: 'instant',
        timeExtent: {
          start: moment(dates[dates.length - 1])
            .startOf('day')
            .toDate(),
          end: moment(dates[dates.length - 1])
            .endOf('day')
            .toDate(),
        },
        fullTimeExtent: {
          start,
          end,
        },
        stops: {
          dates,
        },
      });

      this._landsatTimeslider.watch(
        'timeExtent',
        _debounce(
          (timeExtent: __esri.TimeExtent) => {
            if (moment(timeExtent.start).isSame(timeExtent.end)) {
              timeExtent.start = moment(timeExtent.start).startOf('day').toDate();
              timeExtent.end = moment(timeExtent.end).endOf('day').toDate();
              this._mapview!.timeExtent = timeExtent;
            }
            this._landsatTimeExtent = timeExtent;
            if (settingUpLandsatSlider) {
              settingUpLandsatSlider = false;
            } else {
              this.debouncedUpdateTimeSliders();
            }
          },
          750,
          { trailing: true }
        )
      );
    } else {
      let settingUpSentinelSlider = true;
      if (this._sentinelTimeslider) {
        this._sentinelTimeslider.destroy();
      }
      this._sentinelTimeslider = new TimeSlider({
        container: 'sentinel',
        view: this._mapview,
        mode: 'instant',
        timeExtent: {
          start: moment(dates[dates.length - 1])
            .startOf('day')
            .toDate(),
          end: moment(dates[dates.length - 1])
            .endOf('day')
            .toDate(),
        },
        fullTimeExtent: {
          start,
          end,
        },
        stops: {
          dates,
        },
      });

      this._sentinelTimeslider.watch(
        'timeExtent',
        _debounce(
          (timeExtent: __esri.TimeExtent) => {
            if (moment(timeExtent.start).isSame(timeExtent.end)) {
              timeExtent.start = moment(timeExtent.start).startOf('day').toDate();
              timeExtent.end = moment(timeExtent.end).endOf('day').toDate();
              this._mapview!.timeExtent = timeExtent;
            }
            this._sentinelTimeExtent = timeExtent;
            if (settingUpSentinelSlider) {
              settingUpSentinelSlider = false;
            } else {
              this.debouncedUpdateTimeSliders();
            }
          },
          750,
          { trailing: true }
        )
      );
    }
  };
}

const mapController = new MapController();

//TODO: This is for DEV only and should be removed once we deploy to prod
declare global {
  interface Window {
    mapController: typeof mapController;
  }
}

window.mapController = mapController;

export default mapController;
